// FXM Messenger
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Agent, api, API_BASE, Attachment, Channel, ChannelMembers, DiscoverableWorkspace, Dm, FileItem,
  HelmSuggestion, JoinRequest, Message, MyJoinRequest, MyOrg, PublicUser,
} from './api';
import { AgentStatus, NotifyEvent, RingEvent, SuggestionEvent, TypingSignal, useChannelMessages, useUserStream, useWorkspaceStream } from './realtime';
import { useAuth } from './ChatAuth';
import { CallModal } from './CallModal';
import { ChatDock, DockItem, WorkspaceRoller } from './ChatDock';
import { watchForUpdates } from './update';
import { ConversationSidebar } from './ConversationSidebar';
import { KnowledgeView } from './KnowledgeView';
import { CalendarView } from './Calendar';
import { StickyNotesView } from './StickyNotes';
import { JiraView } from './Jira';
import { ForumBoard } from './ForumBoard';
import { MemberProfileModal } from './MemberProfile';
import { AccountMenu, avatarStyle, hueOf } from './AccountMenu';
import { Settings } from './Settings';
import { Preferences, PrefCat } from './Preferences';
import {
  ActivityView,
  BrowseAppsModal,
  BrowseChannelsModal,
  CallsView,
  DirectoryModal,
  DraftItem,
  DraftsView,
  FilesView,
  PlaceholderView,
  RecentDmsModal,
} from './views';
import { Composer } from './Composer';
import { IncidentBanner, DeclareIncidentModal } from './Incident';
import { ThreadPanel } from './Thread';
import { startScreenCameraClip, ClipSession } from './clip';
import { localTimeHint } from './tz';
import { CallLauncher } from './CallLauncher';
import { FindTimeModal } from './FindTime';
import { CommandPalette, JumpAction } from './CommandPalette';
import { ChannelDetails } from './ChannelDetails';
import { AgentAvatar, AgentFace, ROBOT_VARIANTS, robotVariantOf } from './AgentAvatar';

// True on touch-only devices (phones/tablets): hover UI needs a tap affordance there.
const TOUCH_ONLY = typeof window !== 'undefined' && !!window.matchMedia?.('(hover: none)').matches;
import { ChannelDocsTab } from './DocsTab';
import { SuggestionCards } from './SuggestionCards';
import { PollCard, PromptCard, CreatePollModal, KudosModal, ChecklistModal } from './Collab';
import { parseApproval, ApprovalActions } from './ApprovalCard';
import { ReceiptsModal, ComplianceModal } from './Enterprise';
import { WebhooksModal } from './Webhooks';
import { AdminPanel } from './AdminPanel';
import { FieldAdminModal } from './FieldAdmin';
import { EmbedWidgetsModal } from './EmbedWidgets';
import { EmojiPicker } from './emoji';
import { renderMd } from './markdown';
import { FileBadge, Icon } from './icons';
import {
  channelNotifyLevel,
  convertEmoticons,
  fmtTime,
  getPref,
  hasOpenCodeFence,
  initAudioUnlock,
  isVip,
  isJumbomoji,
  messageAria,
  playCallSound,
  applySkin,
  flashTitle,
  playNotifySound,
  quickReactions,
  recordEmojiUse,
  speakUnavailable,
  setTitleBadge,
  shouldNotify,
  usePrefEffects,
  usePrefSetter,
  dayKey,
  dayLabel,
} from './prefs';
import '../fresh.css';
import './chat.css';

const FALLBACK_QUICK = ['✅', '👀', '🙌'];
type View = 'chat' | 'activity' | 'files' | 'threads' | 'huddles' | 'drafts' | 'knowledge' | 'calendar' | 'people' | 'notes' | 'jira';

const DRAFTS_KEY = 'fxmchat_drafts';
type DraftRec = { text: string; at: number };
const loadDrafts = (): Record<string, DraftRec> => {
  try {
    const raw = JSON.parse(localStorage.getItem(DRAFTS_KEY) || '{}');
    const out: Record<string, DraftRec> = {};
    for (const [k, v] of Object.entries(raw)) {
      out[k] = typeof v === 'string' ? { text: v, at: 0 } : (v as DraftRec);
    }
    return out;
  } catch {
    return {};
  }
};
const saveDraft = (cid: string, text: string) => {
  const d = loadDrafts();
  if (text) d[cid] = { text, at: Date.now() };
  else delete d[cid];
  localStorage.setItem(DRAFTS_KEY, JSON.stringify(d));
};
const getDraftText = (cid: string): string => loadDrafts()[cid]?.text || '';

/** Close a popover on any tap/click outside it. The hover-close (`onMouseLeave`)
 * never fires on touch, which left these popovers with no dismiss path on
 * phones. `selector` should match the popover AND its anchor bar, so tapping
 * the anchor toggles instead of close-then-reopen. */
function useOutsideTapClose(open: boolean, close: () => void, selector: string) {
  useEffect(() => {
    if (!open) return;
    const h = (e: Event) => {
      const t = e.target as Element | null;
      if (t && !t.closest(selector)) close();
    };
    document.addEventListener('pointerdown', h);
    return () => document.removeEventListener('pointerdown', h);
  }, [open, close, selector]);
}

export function ChatApp() {
  const { user, patchUser, logout } = useAuth();
  const [workspaces, setWorkspaces] = useState<Workspaceish[]>([]);
  const [activeWs, setActiveWs] = useState<string | null>(null);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [dms, setDms] = useState<Dm[]>([]);
  const [members, setMembers] = useState<PublicUser[]>([]);
  const [activeCh, setActiveCh] = useState<string | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [view, setView] = useState<View>('chat');
  const [accountOpen, setAccountOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [newMenuOpen, setNewMenuOpen] = useState(false);
  const [newAnchor, setNewAnchor] = useState<DOMRect | null>(null);
  const [incomingCall, setIncomingCall] = useState<RingEvent | null>(null);
  const [pendingCall, setPendingCall] = useState<{ convId: string; video: boolean } | null>(null);
  // The live call is app-level state: it must survive switching conversations,
  // going Home, even switching workspaces — only Leave (or the ring ending) ends it.
  const [activeCall, setActiveCall] = useState<{ convId: string; title: string; video: boolean; initiated: boolean } | null>(null);
  // A newer build is deployed: self-reload when non-disruptive, else show a pill.
  const [updateReady, setUpdateReady] = useState(false);
  const activeCallRef = useRef(activeCall);
  activeCallRef.current = activeCall;
  useEffect(() => watchForUpdates(() => setUpdateReady(true)), []);
  useEffect(() => {
    if (!updateReady) return undefined;
    const safeToReload = () =>
      document.visibilityState === 'hidden' && !activeCallRef.current && !(window as any).__fxDraftPending;
    if (safeToReload()) { window.location.reload(); return undefined; }
    const onVis = () => { if (safeToReload()) window.location.reload(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [updateReady]);
  const [acctAnchor, setAcctAnchor] = useState<DOMRect | null>(null);
  const [moreAnchor, setMoreAnchor] = useState<DOMRect | null>(null);
  useOutsideTapClose(newMenuOpen, () => setNewMenuOpen(false), '.fx-moremenu, .fx-dock2');
  useOutsideTapClose(moreOpen, () => setMoreOpen(false), '.fx-moremenu, .fx-dock2');
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileFor, setProfileFor] = useState<string | null>(null);
  const [prefsCat, setPrefsCat] = useState<PrefCat | null>(null);
  const [directoryOpen, setDirectoryOpen] = useState(false);
  const [draftsTab, setDraftsTab] = useState<'drafts' | 'scheduled' | 'sent'>('drafts');
  const [draftsRev, setDraftsRev] = useState(0);
  const openDrafts = (tab: 'drafts' | 'scheduled' | 'sent' = 'drafts') => {
    setDraftsTab(tab);
    setView('drafts');
  };
  const [adminOpen, setAdminOpen] = useState(false);
  const [createChanOpen, setCreateChanOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [createFmaOpen, setCreateFmaOpen] = useState(false);
  const [inviteFlash, setInviteFlash] = useState('');
  const [appFlash, setAppFlash] = useState('');
  const appToast = (t: string) => { setAppFlash(t); window.setTimeout(() => setAppFlash(''), 1600); };
  const [kudosOpen, setKudosOpen] = useState(false);
  const [complianceOpen, setComplianceOpen] = useState(false);
  const [webhooksOpen, setWebhooksOpen] = useState(false);
  const [adminPanelOpen, setAdminPanelOpen] = useState(false);
  const [adminFieldsOpen, setAdminFieldsOpen] = useState(false);
  const [embedOpen, setEmbedOpen] = useState(false);
  const [approvalsOpen, setApprovalsOpen] = useState(false);
  const [pendingReqCount, setPendingReqCount] = useState(0);
  const [browse, setBrowse] = useState<null | 'channels' | 'dms' | 'apps'>(null);
  const [tokenReveal, setTokenReveal] = useState<{ name: string; token: string } | null>(null);
  // In-app input dialog (replaces window.prompt, which browsers suppress after the first).
  const [inputModal, setInputModal] = useState<{
    title: string;
    label: string;
    placeholder: string;
    submitLabel: string;
    onSubmit: (value: string) => Promise<void>;
  } | null>(null);
  const [addAgentOpen, setAddAgentOpen] = useState(false);
  const [callsEnabled, setCallsEnabled] = useState(false);
  const [activityCount, setActivityCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState<HelmSuggestion[]>([]);

  const set = usePrefSetter(user, patchUser);
  usePrefEffects(user, patchUser);
  // Auto-detect timezone from the browser once, when the profile has none set.
  useEffect(() => {
    if (!user || user.timezone) return;
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz) api.updateProfile({ timezone: tz }).then(patchUser).catch(() => {});
    } catch { /* Intl unavailable — skip */ }
  }, [user?.id, user?.timezone]);
  const p = user?.prefs;

  const reloadWorkspaces = () =>
    api.workspaces().then((ws) => {
      setWorkspaces(ws);
      setActiveWs((cur) => cur ?? ws[0]?.id ?? null);
    });

  useEffect(() => {
    reloadWorkspaces().finally(() => setLoading(false));
    api.callsConfig().then((c) => setCallsEnabled(c.enabled)).catch(() => setCallsEnabled(false));
  }, []);

  const reloadLists = useCallback((wid: string) => {
    api.channels(wid).then((chs) => {
      setChannels(chs);
      // Land on the Deck home (activeCh null) rather than auto-opening a channel.
      setActiveCh((cur) => (chs.some((c) => c.id === cur) ? cur : null));
    });
    api.dms(wid).then(setDms).catch(() => setDms([]));
    api.agents(wid).then(setAgents).catch(() => setAgents([]));
    api.members(wid).then(setMembers).catch(() => setMembers([]));
  }, []);

  useEffect(() => {
    if (!activeWs) {
      setChannels([]); setDms([]); setActiveCh(null); setAgents([]); setMembers([]);
      return;
    }
    reloadLists(activeWs);
  }, [activeWs, reloadLists]);

  // Pending join-request badge (owners/admins only)
  useEffect(() => {
    const ws = workspaces.find((w) => w.id === activeWs);
    const admin = ws?.role === 'owner' || ws?.role === 'admin';
    if (activeWs && admin) {
      api.joinRequests(activeWs).then((r) => setPendingReqCount(r.length)).catch(() => setPendingReqCount(0));
    } else {
      setPendingReqCount(0);
    }
  }, [activeWs, workspaces]);

  // ── Realtime: personal notify stream (badges/sounds) + workspace presence ──
  const activeChRef = useRef<string | null>(null);
  activeChRef.current = view === 'chat' ? activeCh : null;

  const myPats = useMemo(() => {
    const kws = getPref(p, 'notifications.keywords', '')
      .split(',').map((s: string) => s.trim().toLowerCase()).filter(Boolean);
    const names = [user?.name, (user?.name || '').split(' ')[0], user?.email?.split('@')[0]]
      .filter(Boolean).map((n) => `@${String(n).toLowerCase()}`);
    // The group mention pings every member of the channel.
    return [...names, '@team', ...kws];
  }, [user?.name, user?.email, p]);

  const bump = useCallback((e: NotifyEvent, mentioned: boolean) => {
    const apply = (c: Channel): Channel =>
      c.id === e.channel_id
        ? {
            ...c,
            unread: true,
            mentions: (c.mentions || 0) + (mentioned ? 1 : 0),
            last_message_at: e.message.created_at || c.last_message_at,
            // Refresh the card's preview too — it kept showing the previous
            // (often your own) message while only the unread state updated.
            last_message: {
              body: e.message.body || (e.message.attachments?.length ? '📎 attachment' : ''),
              sender_name: (e.message as any).sender_name || 'Someone',
              mine: false,
            },
          }
        : c;
    setChannels((cur) => cur.map(apply));
    setDms((cur) => cur.map((d) => apply(d) as Dm));
  }, []);

  useUserStream(user?.id || null, (e) => {
    const body = (e.message.body || '').toLowerCase();
    // Targeted thread notifies (I'm a participant, or mentioned in the reply)
    // already passed server-side relevance — treat them like a mention so they
    // break through the "DMs & mentions" default instead of dying as chatter.
    const mentioned = myPats.some((pat) => body.includes(pat)) || !!e.targeted;
    const isDm = e.channel_kind === 'dm';
    if (e.channel_id === activeChRef.current) {
      if (getPref(p, 'mark_read.mode', 'left_off_mark') !== 'newest_unread') {
        api.markRead(e.channel_id).catch(() => {});
      }
      return;
    }
    const muted = (getPref(p, 'home.muted', []) as string[]).includes(e.channel_id);
    // A notify for a conversation we don't have yet (e.g. the first DM from someone) —
    // pull the lists so its card appears live on the deck instead of only on refresh.
    // Reload whenever it's unknown (the fetch is idempotent); if it's in another
    // workspace it simply won't be in the active-workspace lists.
    const known = channels.some((c) => c.id === e.channel_id) || dms.some((d) => d.id === e.channel_id);
    if (!known && activeWs) {
      api.channels(activeWs).then(setChannels).catch(() => {});
      api.dms(activeWs).then(setDms).catch(() => {});
    } else {
      bump(e, mentioned);
    }
    const notify = shouldNotify(user, {
      isDm, mentioned, muted, channelId: e.channel_id, senderId: e.message.sender_id,
    });
    if ((mentioned || isDm) && notify) setActivityCount((c) => c + 1);
    if (notify) { playNotifySound(user); flashTitle(user); }
  }, (ring) => {
    if (ring.type === 'call.cancel') {
      // The caller ended the call — stop ringing if this is the one we're ringing for.
      setIncomingCall((cur) => (cur && cur.channel_id === ring.channel_id ? null : cur));
      return;
    }
    if (ring.type === 'call.decline') {
      // A callee declined our outgoing call. In a DM there's only one callee, so end
      // the ringback and say they're unavailable; in a group, others may still join —
      // just note who declined.
      const name = ring.by_name || 'They';
      if (ring.channel_kind === 'dm') {
        setActiveCall((cur) => (cur && cur.convId === ring.channel_id ? null : cur));
        setPendingCall((cur) => (cur && cur.convId === ring.channel_id ? null : cur));
        setInviteFlash(`${name} is currently not available for call`);
        speakUnavailable(name);
      } else {
        setInviteFlash(`${name} declined the call`);
      }
      window.setTimeout(() => setInviteFlash(''), 4500);
      return;
    }
    setIncomingCall(ring);
  }, (we) => {
    if (we.type === 'join.request') {
      setPendingReqCount((c) => c + 1);
    } else if (we.type === 'join.decided' && we.status === 'approved') {
      reloadWorkspaces().then(() => { setActiveWs(we.workspace_id); setView('chat'); });
    }
  }, (rem) => {
    // Calendar event reminder — desktop notification + a sound.
    playNotifySound(user);
    setActivityCount((c) => c + 1);
    try {
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        const when = rem.starts_at ? new Date(rem.starts_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
        new Notification(`${rem.is_call ? '📞 ' : '📅 '}${rem.title}`, {
          body: `Starts ${rem.lead}${when ? ` · ${when}` : ''}${rem.location ? ` · ${rem.location}` : ''}`,
          tag: `event-${rem.id}`,
        });
      } else if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
        Notification.requestPermission().catch(() => {});
      }
    } catch { /* ignore */ }
  }, (se) => {
    setSuggestions((cur) => [
      { ...se.suggestion, workspace_id: '', target_user_id: user?.id || '', status: 'pending' as const, created_at: new Date().toISOString() },
      ...cur,
    ]);
  });

  // Load pending suggestions on workspace change (only when Helm Intelligence is on)
  useEffect(() => {
    if (!activeWs || !user?.ai_features_enabled) return;
    api.mySuggestions('pending', 20).then(setSuggestions).catch(() => {});
  }, [activeWs, user?.ai_features_enabled]);

  useWorkspaceStream(activeWs, (u) => {
    if (u.id === user?.id) {
      patchUser({ ...(user as any), ...u, prefs: user?.prefs, theme: user?.theme });
      return;
    }
    setMembers((cur) => cur.map((m) => (m.id === u.id ? { ...m, ...u } : m)));
    setDms((cur) =>
      cur.map((d) =>
        d.dm_with.type === 'user' && d.dm_with.id === u.id
          ? { ...d, dm_with: { ...d.dm_with, presence: u.presence, status_emoji: u.status_emoji, status_text: u.status_text } }
          : d,
      ),
    );
  });

  // Title badge = anything unread (the setting says "when something is unread" —
  // it previously counted only DM unreads + channel mentions, so ordinary unread
  // channels never badged the tab).
  useEffect(() => {
    const dmUnread = dms.filter((d) => d.unread).length;
    const chUnread = channels.filter((c) => c.unread).length;
    setTitleBadge(user, dmUnread + chUnread);
  }, [dms, channels, user]);

  // Unlock audio on the first user gesture so notification sounds aren't muted.
  useEffect(() => { initAudioUnlock(); }, []);

  // Ring repeatedly while an incoming call banner is showing — but stop after 35s if the
  // call is never answered (a ring must never outlive the call).
  useEffect(() => {
    if (!incomingCall) return undefined;
    playCallSound(user);
    const id = window.setInterval(() => playCallSound(user), 2800);
    const stop = window.setTimeout(() => setIncomingCall(null), 35000);
    return () => { window.clearInterval(id); window.clearTimeout(stop); };
    // keyed on the call identity so an unrelated `user` re-render doesn't restart the ring
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingCall?.channel_id, incomingCall?.from_id]);

  // Keyboard shortcuts: Ctrl+Shift+J files, Ctrl+K search, Esc read, Shift+Esc read-all, Ctrl+/ help
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const uiRef = useRef<{ activeCh: string | null; anyModal: boolean; markAll: () => void; markOne: () => void }>({
    activeCh: null, anyModal: false, markAll: () => {}, markOne: () => {},
  });
  // Assigned post-render (an always-running effect) so it can safely reference
  // handlers declared further down the component body.
  useEffect(() => {
    uiRef.current = {
      activeCh: view === 'chat' ? activeCh : null,
      anyModal: !!(prefsCat || profileOpen || directoryOpen || adminOpen || browse || tokenReveal || accountOpen || newMenuOpen || moreOpen || shortcutsOpen || paletteOpen || embedOpen),
      markAll: () => markAllRead(false),
      markOne: () => {
        const cid = view === 'chat' ? activeCh : null;
        if (cid) markViewed(cid);
      },
    };
  });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 'j') {
        e.preventDefault();
        setView('files');
      } else if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(true);
      } else if ((e.ctrlKey || e.metaKey) && e.key === '/') {
        e.preventDefault();
        setShortcutsOpen((v) => !v);
      } else if (e.key === 'Escape' && !uiRef.current.anyModal) {
        if (e.shiftKey) uiRef.current.markAll();
        else uiRef.current.markOne();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const chatChannel = view === 'chat' ? activeCh : null;
  const { messages, typing, agentStatuses, pollLive, promptLive, reads, hasMore, loadOlder, addLocalEcho, confirmLocalEcho, failLocalEcho } = useChannelMessages(chatChannel);
  const activeWorkspace = workspaces.find((w) => w.id === activeWs);
  const isGuest = activeWorkspace?.role === 'guest';
  const activeConversation: (Channel & Partial<Dm>) | undefined =
    channels.find((c) => c.id === activeCh) || dms.find((d) => d.id === activeCh);

  const markViewed = useCallback(
    (cid: string) => {
      if (getPref(p, 'mark_read.mode', 'left_off_mark') === 'newest_unread') return;
      api.markRead(cid).catch(() => {});
      const clear = (c: Channel): Channel =>
        c.id === cid ? { ...c, unread: false, mentions: 0, last_read_at: new Date().toISOString() } : c;
      setChannels((cur) => cur.map(clear));
      setDms((cur) => cur.map((d) => clear(d) as Dm));
    },
    [p],
  );

  const openConversation = (id: string) => {
    setActiveCh(id);
    setView('chat');
  };

  // Deep link from a copied message link (?c=<channel>&m=<message>): open the
  // conversation once its list has loaded, then scroll to the message.
  const deepLinkDone = useRef(false);
  useEffect(() => {
    if (deepLinkDone.current) return;
    const params = new URLSearchParams(window.location.search);
    const c = params.get('c');
    const m = params.get('m');
    const w = params.get('ws'); // push notifications carry the workspace too
    if (!c) { deepLinkDone.current = true; return; }
    // Target may live in another workspace (e.g. a push while a different ws is active) —
    // switch first; its lists reload and this effect retries.
    if (w && w !== activeWs) { setActiveWs(w); return; }
    const known = channels.some((x) => x.id === c) || dms.some((x) => x.id === c);
    if (!known) return; // retry when the lists change
    deepLinkDone.current = true;
    openConversation(c);
    window.history.replaceState({}, '', window.location.pathname);
    if (m) window.setTimeout(() => document.getElementById(`msg-${m}`)?.scrollIntoView({ block: 'center' }), 700);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channels, dms, activeWs]);

  // Warm path for a tapped push: the service worker posts the deep-link URL to the
  // already-open app (a focus() alone can't change the route). Parse + open it here.
  useEffect(() => {
    const sw = navigator.serviceWorker;
    if (!sw) return undefined;
    const onMsg = (e: MessageEvent) => {
      if ((e.data as any)?.type !== 'notif-open' || !(e.data as any).url) return;
      try {
        const u = new URL((e.data as any).url, window.location.origin);
        const c = u.searchParams.get('c');
        const w = u.searchParams.get('ws');
        const m = u.searchParams.get('m');
        if (!c) return;
        if (w && w !== activeWs) setActiveWs(w);
        openConversation(c);
        if (m) window.setTimeout(() => document.getElementById(`msg-${m}`)?.scrollIntoView({ block: 'center' }), 700);
      } catch { /* ignore malformed */ }
    };
    sw.addEventListener('message', onMsg);
    return () => sw.removeEventListener('message', onMsg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWs]);
  // Accept a workspace invite link (?invite=<code>): join directly, switch to it.
  // The code survives sign-in (ChatAuth only strips otc/reset/verified/login_error).
  const inviteDone = useRef(false);
  useEffect(() => {
    if (inviteDone.current) return;
    const params = new URLSearchParams(window.location.search);
    // The code may be in the URL, or persisted before an auth redirect stripped it.
    let code = params.get('invite');
    if (!code) { try { code = localStorage.getItem('fxmchat_pending_invite'); } catch { code = null; } }
    if (!code) { inviteDone.current = true; return; }
    inviteDone.current = true;
    try { localStorage.removeItem('fxmchat_pending_invite'); } catch { /* ignore */ }
    if (params.has('invite')) window.history.replaceState({}, '', window.location.pathname);
    api.acceptInvite(code)
      .then(async (r) => {
        await reloadWorkspaces();
        setActiveWs(r.id);
        setView('chat');
        setActiveCh(null);
        setInviteFlash(`Joined ${r.name}`);
        window.setTimeout(() => setInviteFlash(''), 2600);
      })
      .catch((e: any) => {
        setInviteFlash(e?.message || 'That invite link is no longer valid');
        window.setTimeout(() => setInviteFlash(''), 3600);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Return from a calendar-connect consent redirect (?calendar=connected / ?calendar_error).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('calendar')) {
      const ok = params.get('calendar') === 'connected';
      setInviteFlash(ok ? '📅 Calendar connected' : 'Calendar connection didn’t complete');
      window.setTimeout(() => setInviteFlash(''), ok ? 2600 : 3600);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.has('calendar_error')) {
      setInviteFlash('Calendar connection didn’t complete — please try again.');
      window.setTimeout(() => setInviteFlash(''), 3600);
      window.history.replaceState({}, '', window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const acceptIncoming = () => {
    if (!incomingCall) return;
    openConversation(incomingCall.channel_id);
    setPendingCall({ convId: incomingCall.channel_id, video: incomingCall.video });
    setIncomingCall(null);
  };
  const openActivity = () => {
    setView('activity');
    setActivityCount(0);
    set('activity_seen_at', new Date().toISOString());
  };
  const createWorkspace = () => {
    setInputModal({
      title: 'Create a workspace',
      label: 'Workspace name',
      placeholder: 'Acme Inc',
      submitLabel: 'Create workspace',
      onSubmit: async (name) => {
        const ws = await api.createWorkspace(name);
        await reloadWorkspaces();
        setActiveWs(ws.id);
      },
    });
  };
  const createChannel = () => {
    if (!activeWs) return;
    setCreateChanOpen(true);
  };
  const doCreateChannel = async (name: string, kind: 'public' | 'private', memberIds: string[]) => {
    if (!activeWs) return;
    try {
      const ch = await api.createChannel(activeWs, name, kind, memberIds);
      setChannels(await api.channels(activeWs));
      setCreateChanOpen(false);
      openConversation(ch.id);
    } catch (ex: any) {
      window.alert(ex?.message || 'Could not create the channel');
    }
  };
  // Presence heartbeat (Teams-style): ping while the tab is visible AND the user
  // touched mouse/keyboard in the last few minutes. Go idle / close the tab and
  // the server flips you to "away" after ~5 quiet minutes.
  useEffect(() => {
    let lastInput = Date.now();
    const onInput = () => { lastInput = Date.now(); };
    window.addEventListener('pointermove', onInput, { passive: true });
    window.addEventListener('keydown', onInput, { passive: true });
    const beat = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastInput < 4 * 60_000) {
        api.heartbeat().catch(() => {});
      }
    };
    beat();
    const t = window.setInterval(beat, 60_000);
    return () => {
      window.clearInterval(t);
      window.removeEventListener('pointermove', onInput);
      window.removeEventListener('keydown', onInput);
    };
  }, []);

  // Own sends: patch the deck previews in place — the notify stream excludes the
  // sender, so without this a just-sent message never shows on Home until reload.
  useEffect(() => {
    const onSent = (e: Event) => {
      const { channelId, body } = (e as CustomEvent).detail || {};
      if (!channelId) return;
      const now = new Date().toISOString();
      const patch = <T extends Channel>(c: T): T =>
        c.id === channelId
          ? { ...c, last_message: { body: body || '📎 attachment', sender_name: user?.name || 'You', mine: true }, last_message_at: now }
          : c;
      setChannels((cur) => cur.map(patch));
      setDms((cur) => cur.map((d) => patch(d) as Dm));
    };
    window.addEventListener('fx:message-sent', onSent);
    return () => window.removeEventListener('fx:message-sent', onSent);
  }, [user?.name]);

  // Someone (possibly on another device) deleted the channel we may be viewing.
  useEffect(() => {
    const onGone = (e: Event) => {
      const cid = (e as CustomEvent).detail?.channelId;
      if (!cid) return;
      if (activeChRef.current === cid) setActiveCh(null);
      if (activeWs) api.channels(activeWs).then(setChannels).catch(() => {});
    };
    // An agent added to a channel changes who can see it — refresh the workspace
    // agent list so it becomes mentionable without a reload.
    const onMembers = () => { if (activeWs) api.agents(activeWs).then(setAgents).catch(() => {}); };
    window.addEventListener('fx:channel-deleted', onGone);
    window.addEventListener('fx:members-changed', onMembers);
    return () => {
      window.removeEventListener('fx:channel-deleted', onGone);
      window.removeEventListener('fx:members-changed', onMembers);
    };
  }, [activeWs]);
  const deleteChannel = async (cid: string) => {
    if (!window.confirm('Delete this channel? All its messages are permanently removed for everyone.')) return;
    try {
      await api.deleteChannel(cid);
      setActiveCh(null);
      if (activeWs) setChannels(await api.channels(activeWs));
    } catch (ex: any) {
      window.alert(ex?.message || 'Could not delete the channel');
    }
  };
  const createAgent = () => {
    if (!activeWs) return;
    setInputModal({
      title: 'Add an agent',
      label: 'Agent name (e.g. your FMA)',
      placeholder: 'e.g. Beetle',
      submitLabel: 'Create agent',
      onSubmit: async (name) => {
        const res = await api.createAgent(activeWs, name);
        setAgents(await api.agents(activeWs));
        setTokenReveal({ name: res.name, token: res.token });
      },
    });
  };
  const openDm = async (type: 'user' | 'agent', id: string) => {
    if (!activeWs) return;
    try {
      const dm = await api.createDm(activeWs, type, id);
      setDms(await api.dms(activeWs));
      setDirectoryOpen(false);
      openConversation(dm.id);
    } catch (ex: any) {
      // A silent failure here looks like a dead button — say what went wrong.
      window.alert(ex?.message || 'Could not open the conversation');
      return;
    }
  };
  const addAgentToChannel = () => {
    if (!activeWs || !activeCh) return;
    if (agents.length === 0) {
      createAgent();
      return;
    }
    setAddAgentOpen(true);
  };
  const doAddAgentToChannel = async (agentId: string, canPost: boolean) => {
    if (!activeCh) return;
    await api.addAgentToChannel(activeCh, agentId, canPost);
    setAddAgentOpen(false);
  };
  const leaveChannel = async (cid: string) => {
    if (!user?.id || !window.confirm('Leave this channel?')) return;
    try {
      await api.removeChannelPerson(cid, user.id);
      setActiveCh(null);
      if (activeWs) setChannels(await api.channels(activeWs));
    } catch (ex: any) {
      window.alert(ex?.message || 'Could not leave');
    }
  };

  // Starred (prefs.starred = conversation ids)
  const starred: string[] = getPref(p, 'starred', []);
  const toggleStar = (id: string) =>
    set('starred', starred.includes(id) ? starred.filter((s) => s !== id) : [...starred, id]);
  const mutedIds: string[] = getPref(p, 'home.muted', []);
  const toggleMute = (id: string) =>
    set('home.muted', mutedIds.includes(id) ? mutedIds.filter((s) => s !== id) : [...mutedIds, id]);

  const markAllRead = async (skipConfirm = false) => {
    if (!activeWs) return;
    if (!skipConfirm && getPref(p, 'mark_read.confirm_all', true) && !window.confirm('Mark everything as read?')) {
      return;
    }
    await api.readAll(activeWs).catch(() => {});
    const clear = (c: Channel): Channel => ({ ...c, unread: false, mentions: 0 });
    setChannels((cur) => cur.map(clear));
    setDms((cur) => cur.map((d) => clear(d) as Dm));
  };

  const markUnreadAt = useCallback(async (cid: string, at: string) => {
    const t = new Date(new Date(at).getTime() - 1).toISOString();
    await api.markRead(cid, t).catch(() => {});
    const flag = (c: Channel): Channel => (c.id === cid ? { ...c, unread: true, last_read_at: t } : c);
    setChannels((cur) => cur.map(flag));
    setDms((cur) => cur.map((d) => flag(d) as Dm));
  }, []);
  if (loading) return <div className="fx-login"><div className="fx-login-note">Loading…</div></div>;
  if (workspaces.length === 0) {
    return <Onboarding onCreated={(ws) => { setWorkspaces([ws]); setActiveWs(ws.id); }} />;
  }

  const initial = (s?: string) => (s?.trim()?.[0] || '?').toUpperCase();
  const showBadges = getPref(p, 'navigation.show_unread_badges', true);
  const dockBottom = getPref(p, 'appearance.dock_position', 'left') === 'bottom';
  // While inside one conversation there is no other signal that a DIFFERENT
  // channel/DM has news — surface it on the always-visible dock: Home carries
  // everything unread except the open conversation; DMs carries DM unreads.
  const dmUnreadCount = dms.filter((d) => d.unread && d.id !== activeCh).length;
  const otherUnreadCount = channels.filter((c) => c.unread && c.id !== activeCh).length + dmUnreadCount;

  const openNewMenu = (e?: React.MouseEvent) => {
    setNewAnchor(e?.currentTarget?.getBoundingClientRect() ?? null);
    setNewMenuOpen((v) => !v);
  };
  const dockItems: DockItem[] = [
    { key: 'new', label: 'New', kind: 'add', onClick: openNewMenu },
    { key: 'search', label: 'Search (Ctrl/⌘ K)', kind: 'nav', icon: 'search', onClick: () => setPaletteOpen(true) },
    { key: 'sep1' },
    ...(getPref(p, 'navigation.show_home', true)
      ? [{
          key: 'home', label: 'Home', kind: 'nav' as const, icon: 'grid', active: view === 'chat' && !activeCh,
          badge: showBadges ? otherUnreadCount : 0,
          onClick: () => { setView('chat'); setActiveCh(null); },
        }]
      : []),
    // Narrowed default surface (Matt: "just channels and DMs, hide the rest").
    // Everything below is per-user re-enableable and still reachable under More.
    ...(getPref(p, 'navigation.show_activity', false)
      ? [{
          key: 'activity', label: 'Activity', kind: 'nav' as const, icon: 'activity',
          active: view === 'activity', badge: showBadges ? activityCount : 0, onClick: openActivity,
        }]
      : []),
    ...(getPref(p, 'navigation.show_dms', true)
      ? [{
          key: 'dms', label: 'Direct messages', kind: 'nav' as const, icon: 'dm',
          badge: showBadges ? dmUnreadCount : 0,
          onClick: () => setBrowse('dms'),
        }]
      : []),
    // Files stays (Matt: keep Files). Calendar/Tasks&Notes/Activity/Knowledge/
    // Incidents/Forums/Rituals/Jira are hidden from the frontend for now — fewer
    // surfaces, fewer failure points ahead of client migration.
    ...(getPref(p, 'navigation.show_files', true)
      ? [{
          key: 'files', label: 'Files', kind: 'nav' as const, icon: 'files',
          active: view === 'files', onClick: () => setView('files'),
        }]
      : []),
    ...(getPref(p, 'navigation.show_more', true)
      ? [{
          key: 'more', label: 'More', kind: 'nav' as const, icon: 'more',
          onClick: (e?: React.MouseEvent) => {
            setMoreAnchor(e?.currentTarget?.getBoundingClientRect() ?? null);
            setMoreOpen((v) => !v);
          },
        }]
      : []),
    { key: 'spacer' },
    { key: 'settings', label: 'Settings', kind: 'nav', icon: 'gear', onClick: () => setPrefsCat('notifications') },
    {
      key: 'me', label: `${user?.name || user?.email} — account`, kind: 'avatar',
      glyph: initial(user?.name || user?.email), presence: user?.presence,
      onClick: (e?: React.MouseEvent) => {
        setAcctAnchor(e?.currentTarget?.getBoundingClientRect() ?? null);
        setAccountOpen((v) => !v);
      },
    },
  ];

  // Anchor a dock-triggered popover to its icon, wherever the dock lives.
  const dockMenuStyle = (anchor: DOMRect | null, menuW: number): React.CSSProperties => {
    if (!anchor) return {};
    const gap = 12;
    const pad = 8;
    if (dockBottom) {
      const left = Math.min(
        Math.max(pad, anchor.left + anchor.width / 2 - menuW / 2),
        window.innerWidth - menuW - pad,
      );
      return { position: 'fixed', left, bottom: window.innerHeight - anchor.top + gap, top: 'auto', right: 'auto' };
    }
    // left rail: to the right of the icon, growing up from its bottom edge
    return {
      position: 'fixed',
      left: anchor.right + gap,
      bottom: window.innerHeight - anchor.bottom,
      top: 'auto',
      right: 'auto',
    };
  };

  // The New menu opens from a top-anchored trigger (dock + / deck header), so it
  // grows DOWN from the anchor rather than up like the dock popovers.
  const menuBelowStyle = (anchor: DOMRect | null, menuW: number): React.CSSProperties => {
    if (!anchor) return {};
    const gap = 8;
    const pad = 10;
    return {
      position: 'fixed',
      left: Math.min(anchor.right + gap, window.innerWidth - menuW - pad),
      top: Math.min(anchor.top, window.innerHeight - 340),
      width: menuW,
    };
  };

  const muted: string[] = getPref(p, 'home.muted', []);
  const hideMuted = getPref(p, 'home.hide_muted', true);
  const showMode = getPref(p, 'home.show_conversations', 'all');
  const RECENT_MS = 30 * 24 * 3600 * 1000;
  const passShow = (c: Channel) => {
    if (showMode === 'unreads') return !!c.unread;
    if (showMode === 'mentions') return (c.mentions || 0) > 0;
    if (showMode === 'recent') {
      return !c.last_message_at || Date.now() - new Date(c.last_message_at).getTime() < RECENT_MS;
    }
    return true;
  };
  const sortMode = getPref(p, 'home.sort', 'alpha');
  const mentionsTop = getPref(p, 'home.mentions_top', true);
  const onlineTop = getPref(p, 'home.online_dms_top', true);
  const cmpAlpha = (a: Channel, b: Channel) => (a.name || '').localeCompare(b.name || '');
  const cmpRecent = (a: Channel, b: Channel) => (b.last_message_at || '').localeCompare(a.last_message_at || '');
  const sortChannels = (list: Channel[]) => {
    const base = sortMode === 'recent' ? cmpRecent : cmpAlpha; // 'mixed' => channels A–Z
    return [...list].sort((a, b) => {
      if (mentionsTop) {
        const d = ((b.mentions || 0) > 0 ? 1 : 0) - ((a.mentions || 0) > 0 ? 1 : 0);
        if (d) return d;
      }
      return base(a, b);
    });
  };
  const sortDms = (list: Dm[]) => {
    const base = sortMode === 'alpha' ? cmpAlpha : cmpRecent; // 'mixed' => DMs by recent
    return [...list].sort((a, b) => {
      if (mentionsTop) {
        const d = (b.unread ? 1 : 0) - (a.unread ? 1 : 0);
        if (d) return d;
      }
      if (onlineTop) {
        const d = (b.dm_with.presence !== 'away' ? 1 : 0) - (a.dm_with.presence !== 'away' ? 1 : 0);
        if (d) return d;
      }
      return base(a, b);
    });
  };

  const unmuted = (c: Channel) => !(hideMuted && muted.includes(c.id)) || !!c.unread;
  const shownChannels = sortChannels(channels.filter(passShow).filter(unmuted));
  const shownDms = sortDms(dms.filter(passShow));
  const starredConvos = [...channels, ...dms].filter((c) => starred.includes(c.id));

  void draftsRev; // re-read localStorage drafts whenever this bumps
  const draftItems: DraftItem[] = Object.entries(loadDrafts())
    .map(([cid, rec]): DraftItem | null => {
      if (!rec.text.trim()) return null;
      const dm = dms.find((d) => d.id === cid);
      if (dm) {
        return {
          channelId: cid, text: rec.text, at: rec.at, kind: 'dm',
          label: dm.name || '?', sub: `${dm.name} and you`, party: dm.dm_with ?? '',
        };
      }
      const ch = channels.find((c) => c.id === cid);
      return {
        channelId: cid, text: rec.text, at: rec.at, kind: 'channel',
        label: ch ? `# ${ch.name}` : 'conversation', sub: 'Only visible to you',
        chKind: ch?.kind,
      };
    })
    .filter((d): d is DraftItem => !!d)
    .sort((a, b) => (b.at || 0) - (a.at || 0));
  const deleteDraftItem = (cid: string) => {
    saveDraft(cid, '');
    setDraftsRev((r) => r + 1);
  };

  return (
    <div className={`fx-app${dockBottom ? ' is-dock-bottom' : ''}`}>
      <ChatDock
        orientation={dockBottom ? 'horizontal' : 'vertical'}
        header={
          <WorkspaceRoller
            workspaces={workspaces}
            activeId={activeWs}
            onSwitch={(id) => { setActiveWs(id); setView('chat'); }}
            orientation={dockBottom ? 'horizontal' : 'vertical'}
          />
        }
        items={dockItems}
      />

      {updateReady && (
        <button type="button" className="fx-update-pill" onClick={() => window.location.reload()}
          title="A newer version of Helm was deployed — reload to get it">
          ↻ Helm updated — Reload
        </button>
      )}

      {view === 'activity' ? (
        <ActivityView workspaceId={activeWs} onOpenChannel={openConversation} />
      ) : view === 'files' ? (
        <FilesView workspaceId={activeWs} />
      ) : view === 'drafts' ? (
        <DraftsView
          drafts={draftItems}
          initialTab={draftsTab}
          introDismissed={getPref(p, 'drafts_intro_dismissed', false)}
          onDismissIntro={() => set('drafts_intro_dismissed', true)}
          onOpenConversation={openConversation}
          onDeleteDraft={deleteDraftItem}
        />
      ) : view === 'threads' ? (
        <PlaceholderView title="Threads" note="Reply on any message (its ⋯ menu → Reply in thread) to start a thread. A unified threads inbox is coming next." />
      ) : view === 'knowledge' ? (
        <KnowledgeView workspaceId={activeWs} channels={channels} />
      ) : view === 'calendar' ? (
        <CalendarView workspaceId={activeWs} members={members} channels={channels} meId={user?.id} />
      ) : view === 'notes' ? (
        <StickyNotesView meName={user?.name?.split(' ')[0]} workspaceId={activeWs}
          onOpenChannel={(cid) => { setView('chat'); setActiveCh(cid); }} />
      ) : view === 'jira' ? (
        <JiraView />
      ) : view === 'people' && activeWs ? (
        <PeopleView workspaceId={activeWs} workspaceName={activeWorkspace?.name || ''} agents={agents} channels={channels}
          canManage={activeWorkspace?.role === 'owner' || activeWorkspace?.role === 'admin'}
          pendingCount={pendingReqCount} onDm={openDm} onOpenProfile={setProfileFor} />
      ) : view === 'huddles' ? (
        <CallsView channels={channels} dms={dms} members={members} meId={user?.id} callsEnabled={callsEnabled} av={getPref(p, 'av', {})} />
      ) : (
        <div className={`fx-chatwrap${activeConversation ? ' has-active' : ''}`}>
          <ConversationSidebar
            agents={agents}
            channels={shownChannels}
            dms={shownDms}
            activeId={activeCh}
            meId={user?.id}
            onOpenChannel={openConversation}
            onOpenAgent={(id) => openDm('agent', id)}
            onOpenDm={openConversation}
            onNewChannel={createChannel}
            onNewDm={() => setDirectoryOpen(true)}
          />
          {activeConversation ? (
        <ChatView
          key={activeCh || 'none'}
          onHome={() => setActiveCh(null)}
          conversation={activeConversation}
          messages={messages}
          hasMore={hasMore}
          loadOlder={loadOlder}
          addLocalEcho={addLocalEcho}
          confirmLocalEcho={confirmLocalEcho}
          failLocalEcho={failLocalEcho}
          typing={typing}
          agentStatuses={agentStatuses}
          pollLive={pollLive}
          promptLive={promptLive}
          reads={reads}
          user={user}
          members={members}
          agents={agents}
          channels={channels}
          dms={dms}
          callsEnabled={callsEnabled}
          workspaceId={activeWs || ''}
          canManageWorkspace={activeWorkspace?.role === 'owner' || activeWorkspace?.role === 'admin'}
          isStarred={!!activeConversation && starred.includes(activeConversation.id)}
          onToggleStar={() => activeConversation && toggleStar(activeConversation.id)}
          isMuted={!!activeConversation && mutedIds.includes(activeConversation.id)}
          onToggleMute={() => activeConversation && toggleMute(activeConversation.id)}
          onOpenSearch={() => setPaletteOpen(true)}
          onReloadMembers={() => {
            if (!activeWs) return;
            api.members(activeWs).then(setMembers).catch(() => {});
            api.agents(activeWs).then(setAgents).catch(() => {});  // agents are mentionable too
          }}
          onReloadChannels={() => activeWs && api.channels(activeWs).then(setChannels).catch(() => {})}
          onOpenProfile={setProfileFor}
          onAddAgentToChannel={addAgentToChannel}
          onViewed={markViewed}
          onViewScheduled={() => openDrafts('scheduled')}
          onMarkUnread={markUnreadAt}
          onOpenConversation={openConversation}
          onDm={openDm}
          onLeaveChannel={leaveChannel}
          onDeleteChannel={deleteChannel}
          suggestions={suggestions}
          onSuggestionResolved={(id) => setSuggestions((cur) => cur.filter((s) => s.id !== id))}
          pendingCall={pendingCall}
          onPendingConsumed={() => setPendingCall(null)}
          onKudos={() => setKudosOpen(true)}
          inCall={!!activeCall}
          onStartCall={setActiveCall}
        />
          ) : (
            <main className="fx-chat fx-main--empty">
              <div className="fx-empty-big">Pick a conversation, or start one from the sidebar.</div>
            </main>
          )}
        </div>
      )}

      {newMenuOpen && (
        <div className="fx-moremenu" style={menuBelowStyle(newAnchor, 236)} onMouseLeave={() => setNewMenuOpen(false)}>
          {!isGuest && (
            <button type="button" className="fx-acct-item" onClick={() => { setNewMenuOpen(false); createChannel(); }}><Icon name="hash" size={15} /> New channel</button>
          )}
          <button type="button" className="fx-acct-item" onClick={() => { setNewMenuOpen(false); setDirectoryOpen(true); }}><Icon name="dm" size={15} /> New message…</button>
          <button type="button" className="fx-acct-item" onClick={() => { setNewMenuOpen(false); setBrowse('dms'); }}><Icon name="clock" size={15} /> Recent messages</button>
          {!isGuest && (
            <button type="button" className="fx-acct-item" onClick={() => { setNewMenuOpen(false); createAgent(); }}><Icon name="bolt" size={15} /> Add an agent</button>
          )}
          {!isGuest && (
            <button type="button" className="fx-acct-item" onClick={() => { setNewMenuOpen(false); setCreateFmaOpen(true); }}><Icon name="star" size={15} /> Create an FMA agent</button>
          )}
          {!isGuest && (
            <>
              <div className="fx-acct-sep" />
              <button type="button" className="fx-acct-item" onClick={() => { setNewMenuOpen(false); setBrowse('channels'); }}><Icon name="browse" size={15} /> Browse channels</button>
              <button type="button" className="fx-acct-item" onClick={() => { setNewMenuOpen(false); setBrowse('apps'); }}><Icon name="apps" size={15} /> Browse apps</button>
            </>
          )}
          <div className="fx-acct-sep" />
          <button type="button" className="fx-acct-item" onClick={() => { setNewMenuOpen(false); createWorkspace(); }}><Icon name="plus" size={15} /> Create workspace</button>
        </div>
      )}

      {moreOpen && (
        <div className="fx-moremenu" style={dockMenuStyle(moreAnchor, 220)} onMouseLeave={() => setMoreOpen(false)}>
          <button type="button" className="fx-acct-item" onClick={() => { markAllRead(); setMoreOpen(false); }}><Icon name="markread" size={15} /> Mark all as read</button>
          <div className="fx-acct-sep" />
          {/* Secondary surfaces. Activity/Knowledge/Calendar/Tasks&Notes/Jira
              hidden from the frontend for now (see dock note). */}
          <button type="button" className="fx-acct-item" onClick={() => { setView('people'); setMoreOpen(false); }}><Icon name="people" size={15} /> People</button>
          <button type="button" className="fx-acct-item" onClick={() => { setView('huddles'); setMoreOpen(false); }}><Icon name="phone" size={15} /> Calls</button>
          <button type="button" className="fx-acct-item" onClick={() => { openDrafts('drafts'); setMoreOpen(false); }}><Icon name="compose" size={15} /> Drafts &amp; sent</button>
          <div className="fx-acct-sep" />
          {/* Invite/Join moved to People. The four admin tools are consolidated into one Admin panel. */}
          {!(activeWorkspace?.role === 'owner' || activeWorkspace?.role === 'admin') && (
            <button type="button" className="fx-acct-item" onClick={() => { setAdminOpen(true); setMoreOpen(false); }}><Icon name="shield" size={15} /> Bots &amp; apps</button>
          )}
          {(activeWorkspace?.role === 'owner' || activeWorkspace?.role === 'admin') && (
            <button type="button" className="fx-acct-item" onClick={() => { setAdminPanelOpen(true); setMoreOpen(false); }}>
              <Icon name="shield" size={15} /> Admin
            </button>
          )}
          <div className="fx-acct-sep" />
          <button type="button" className="fx-acct-item" onClick={() => { set('appearance.color_mode', user?.theme === 'light' ? 'dark' : 'light'); setMoreOpen(false); }}>
            <Icon name="moon" size={15} /> {user?.theme === 'light' ? 'Switch to dark' : 'Switch to light'}
          </button>
        </div>
      )}

      {accountOpen && user && (
        <AccountMenu
          user={user}
          style={dockMenuStyle(acctAnchor, 300)}
          onOpenProfile={() => setProfileOpen(true)}
          onOpenPreferences={(cat) => setPrefsCat((cat as PrefCat) || 'notifications')}
          onOpenDownloads={() => setView('files')}
          onClose={() => setAccountOpen(false)}
        />
      )}
      {profileOpen && (
        <Settings
          initialTab="account"
          onClose={() => setProfileOpen(false)}
          workspaceId={activeWs || undefined}
          canManageFields={activeWorkspace?.role === 'owner' || activeWorkspace?.role === 'admin'}
        />
      )}
      {profileFor && activeWs && (
        <MemberProfileModal
          workspaceId={activeWs}
          userId={profileFor}
          meId={user?.id}
          onClose={() => setProfileFor(null)}
          onDm={openDm}
        />
      )}
      {prefsCat && <Preferences initialCat={prefsCat} onClose={() => setPrefsCat(null)} />}
      {directoryOpen && activeWs && (
        <DirectoryModal
          workspaceId={activeWs}
          agents={agents}
          onDm={openDm}
          canManage={activeWorkspace?.role === 'owner' || activeWorkspace?.role === 'admin'}
          pendingCount={pendingReqCount}
          onInvite={() => { setDirectoryOpen(false); setInviteOpen(true); }}
          onJoinRequests={() => { setDirectoryOpen(false); setApprovalsOpen(true); }}
          onClose={() => setDirectoryOpen(false)}
        />
      )}
      {browse === 'channels' && (
        <BrowseChannelsModal
          channels={channels}
          muted={mutedIds}
          onOpen={openConversation}
          onToggleMute={toggleMute}
          onCreate={createChannel}
          onClose={() => setBrowse(null)}
        />
      )}
      {browse === 'dms' && (
        <RecentDmsModal dms={dms} onOpen={openConversation} onStart={() => setDirectoryOpen(true)} onClose={() => setBrowse(null)} />
      )}
      {browse === 'apps' && (
        <BrowseAppsModal agents={agents} onDm={openDm} onCreate={createAgent} onClose={() => setBrowse(null)} />
      )}
      {/* Admin hub renders BEFORE its subtools so they stack on top — closing a
          tool lands back on Admin (navigation by stacking, no per-tool back). */}
      {adminPanelOpen && (
        <AdminPanel
          onClose={() => setAdminPanelOpen(false)}
          onBots={() => setAdminOpen(true)}
          onFields={() => setAdminFieldsOpen(true)}
          onCompliance={() => setComplianceOpen(true)}
          onWebhooks={() => setWebhooksOpen(true)}
          onWidget={() => setEmbedOpen(true)}
          workspaceId={activeWs}
          features={activeWorkspace?.features || {}}
          onFeaturesChanged={(f) => setWorkspaces((cur) => cur.map((w) => (w.id === activeWs ? { ...w, features: f } : w)))}
        />
      )}
      {adminOpen && activeWs && (
        <AdminModal
          wid={activeWs}
          agents={agents}
          onBack={() => setAdminOpen(false)}
          onCreate={createAgent}
          onRotate={async (a) => {
            const res = await api.rotateAgentToken(a.id);
            setTokenReveal({ name: res.name, token: res.token });
          }}
          onReloadAgents={() => { if (activeWs) api.agents(activeWs).then(setAgents).catch(() => {}); }}
          onClose={() => setAdminOpen(false)}
        />
      )}
      {approvalsOpen && activeWs && (
        <ApprovalsModal
          workspaceId={activeWs}
          onClose={() => setApprovalsOpen(false)}
          onCountChange={setPendingReqCount}
        />
      )}
      {inviteOpen && activeWs && (
        <InviteModal
          wid={activeWs}
          workspaceName={activeWorkspace?.name || 'this workspace'}
          channels={channels}
          onClose={() => setInviteOpen(false)}
        />
      )}
      {activeCall && (
        <CallModal
          conversationId={activeCall.convId}
          title={activeCall.title}
          av={getPref(p, 'av', {})}
          initialCamera={activeCall.video}
          initiated={activeCall.initiated}
          meId={user?.id}
          members={members}
          onClose={() => {
            // If we started this call, tell anyone still ringing that it's over.
            if (activeCall.initiated) api.cancelCall(activeCall.convId).catch(() => {});
            setActiveCall(null);
          }}
        />
      )}
      {inviteFlash && <div className="fx-flash" role="status">{inviteFlash}</div>}
      {appFlash && <div className="fx-flash" role="status">{appFlash}</div>}
      {createFmaOpen && <CreateFmaModal onClose={() => setCreateFmaOpen(false)} />}
      {createChanOpen && activeWs && (
        <CreateChannelModal
          members={members}
          meId={user?.id}
          onCreate={doCreateChannel}
          onClose={() => setCreateChanOpen(false)}
        />
      )}
      {kudosOpen && activeWs && (
        <KudosModal wid={activeWs} members={members} meId={user?.id} onClose={() => setKudosOpen(false)} />
      )}
      {complianceOpen && activeWs && (
        <ComplianceModal wid={activeWs} onClose={() => setComplianceOpen(false)} />
      )}
      {webhooksOpen && activeWs && (
        <WebhooksModal wid={activeWs} channels={channels} onClose={() => setWebhooksOpen(false)} />
      )}
      {adminFieldsOpen && activeWs && (
        <FieldAdminModal workspaceId={activeWs} onClose={() => setAdminFieldsOpen(false)} />
      )}
      {embedOpen && activeWs && (
        <EmbedWidgetsModal wid={activeWs} onClose={() => setEmbedOpen(false)} />
      )}
      {tokenReveal && <AgentTokenModal data={tokenReveal} onClose={() => setTokenReveal(null)} />}
      {inputModal && <InputModal {...inputModal} onClose={() => setInputModal(null)} />}
      {addAgentOpen && (
        <AddAgentToChannelModal
          agents={agents}
          onAdd={doAddAgentToChannel}
          onNewAgent={() => { setAddAgentOpen(false); createAgent(); }}
          onClose={() => setAddAgentOpen(false)}
        />
      )}
      {shortcutsOpen && <ShortcutsModal onClose={() => setShortcutsOpen(false)} />}
      {incomingCall && (
        <div className="fx-incoming">
          <span className="fx-incoming-ic"><Icon name={incomingCall.video ? 'video' : 'phone'} size={18} /></span>
          <div className="fx-incoming-txt">
            <div className="fx-incoming-title">{incomingCall.from_name} is calling</div>
            <div className="fx-incoming-sub">
              {incomingCall.video ? 'Video' : 'Audio'} call ·{' '}
              {incomingCall.channel_kind === 'dm' ? 'Direct message' : `# ${incomingCall.channel_name}`}
            </div>
          </div>
          <button type="button" className="fx-incoming-decline" onClick={() => {
            // Tell the caller we declined so their ringback stops.
            api.declineCall(incomingCall.channel_id, incomingCall.from_id).catch(() => {});
            setIncomingCall(null);
          }}>Decline</button>
          <button type="button" className="fx-incoming-join" onClick={acceptIncoming}>
            <Icon name="phone" size={14} /> Join
          </button>
        </div>
      )}
      {paletteOpen && activeWs && (
        <CommandPalette
          workspaceId={activeWs}
          channels={channels}
          dms={dms}
          members={members}
          agents={agents}
          onJump={(a: JumpAction) => {
            if (a.kind === 'open') openConversation(a.id);
            else openDm(a.memberType, a.id);
          }}
          onClose={() => setPaletteOpen(false)}
        />
      )}
    </div>
  );
}

function ShortcutsModal({ onClose }: { onClose: () => void }) {
  const rows: { keys: string[]; extra?: string; label: string }[] = [
    { keys: ['Ctrl', 'K'], label: 'Find a conversation' },
    { keys: ['Ctrl', 'Shift', 'J'], label: 'Open Files / Downloads' },
    { keys: ['Esc'], label: 'Mark the current conversation as read' },
    { keys: ['Shift', 'Esc'], label: 'Mark everything as read' },
    { keys: ['Alt'], extra: '+ click message', label: 'Mark a message as unread from there' },
    { keys: ['↑'], label: 'Edit your last message (configurable in Accessibility)' },
    { keys: ['Ctrl', '↑'], label: 'Always edit your last message' },
    { keys: ['Shift', 'Enter'], label: 'New line in the composer' },
    { keys: ['Ctrl', 'Shift', 'Space'], label: 'Mute / unmute while on a call' },
    { keys: ['Ctrl', '/'], label: 'Show this list' },
  ];
  return (
    <div className="fx-modal-backdrop" onClick={onClose}>
      <div className="fx-modal fx-dir" onClick={(e) => e.stopPropagation()}>
        <div className="fx-login-title">Keyboard shortcuts</div>
        {rows.map((r) => (
          <div className="fx-dir-row" key={r.label}>
            <span className="fx-shortcut-keys">
              {r.keys.map((k) => <span className="fx-kbd" key={k}>{k}</span>)}
              {r.extra && <span className="fx-acct-value"> {r.extra}</span>}
            </span>
            <span className="fx-dir-name">{r.label}</span>
          </div>
        ))}
        <div className="fx-modal-actions">
          <button type="button" className="fx-login-back" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

function ChannelCanvasTab({
  conversationId,
  onToast,
}: {
  conversationId: string;
  onToast: (t: string) => void;
}) {
  const [body, setBody] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);
  // Auto-grow the editor to fit its content, so the bottom of a long doc is never
  // hidden behind an inner scrollbar — the tab pane scrolls instead.
  const autosize = () => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${ta.scrollHeight}px`;
  };
  useEffect(() => { if (editing) autosize(); }, [editing, draft]);
  useEffect(() => {
    let alive = true;
    setBody(null);
    setEditing(false);
    api
      .getCanvas(conversationId)
      .then((r) => { if (alive) { setBody(r.body); setDraft(r.body); } })
      .catch(() => alive && setBody(''));
    return () => { alive = false; };
  }, [conversationId]);
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.setCanvas(conversationId, draft);
      setBody(r.body);
      setEditing(false);
      onToast('Canvas saved');
    } catch (ex: any) {
      window.alert(ex?.message || 'Could not save');
    } finally {
      setBusy(false);
    }
  };
  if (body === null) return <div className="fx-tabpane"><div className="fx-empty-small">Loading…</div></div>;
  return (
    <div className="fx-tabpane fx-canvas">
      <div className="fx-canvas-bar">
        <div className="fx-canvas-title"><Icon name="compose" size={15} /> Vault</div>
        {editing ? (
          <div className="fx-canvas-actions">
            <button type="button" className="fx-login-back" onClick={() => { setDraft(body); setEditing(false); }}>Cancel</button>
            <button type="button" className="fx-sendbtn" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
          </div>
        ) : (
          <button type="button" className="fx-head-action" onClick={() => { setDraft(body); setEditing(true); }}>
            <Icon name="compose" size={13} /> Edit
          </button>
        )}
      </div>
      {editing ? (
        <textarea
          ref={taRef}
          className="fx-canvas-editor"
          value={draft}
          onChange={(e) => { setDraft(e.target.value); autosize(); }}
          placeholder="Write shared notes, links, docs for this channel… (markdown supported)"
          autoFocus
        />
      ) : body.trim() ? (
        <div className="fx-canvas-doc">{renderMd(body, { colorSwatches: true, emojiPlain: false, linkMedia: true })}</div>
      ) : (
        <div className="fx-canvas-empty">
          <div className="fx-empty-big">No canvas yet</div>
          <p>A canvas is a shared, editable doc for this channel — notes, links, onboarding, anything. Click <b>Edit</b> to start.</p>
        </div>
      )}
    </div>
  );
}

function ChannelFilesTab({
  workspaceId,
  channelId,
  inlineMedia,
}: {
  workspaceId: string;
  channelId: string;
  inlineMedia: boolean;
}) {
  const [files, setFiles] = useState<FileItem[] | null>(null);
  useEffect(() => {
    let alive = true;
    setFiles(null);
    if (!workspaceId) { setFiles([]); return undefined; }
    api
      .files(workspaceId)
      .then((all) => { if (alive) setFiles(all.filter((f) => f.channel_id === channelId)); })
      .catch(() => alive && setFiles([]));
    return () => { alive = false; };
  }, [workspaceId, channelId]);
  if (files === null) return <div className="fx-tabpane"><div className="fx-empty-small">Loading…</div></div>;
  if (files.length === 0) {
    return (
      <div className="fx-tabpane">
        <div className="fx-canvas-empty">
          <div className="fx-empty-big">No files or links yet</div>
          <p>Files and links shared in this channel show up here.</p>
        </div>
      </div>
    );
  }
  return (
    <div className="fx-tabpane fx-files-tab">
      {files.map((f) => (
        <a key={f.id} className="fx-file-card" href={f.url} target="_blank" rel="noreferrer">
          {inlineMedia && (f.mime || '').startsWith('image/') ? (
            <img className="fx-file-thumb" src={f.url} alt={f.alt || f.name} />
          ) : (
            <span className="fx-file-ic"><FileBadge name={f.name} size={34} /></span>
          )}
          <span className="fx-file-meta">
            <span className="fx-file-name">{f.name || 'File'}</span>
            <span className="fx-file-sub">{f.created_at ? new Date(f.created_at).toLocaleDateString() : ''}</span>
          </span>
        </a>
      ))}
    </div>
  );
}

function InputModal({
  title,
  label,
  placeholder,
  submitLabel,
  onSubmit,
  onClose,
}: {
  title: string;
  label: string;
  placeholder: string;
  submitLabel: string;
  onSubmit: (value: string) => Promise<void>;
  onClose: () => void;
}) {
  const [val, setVal] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!val.trim() || busy) return;
    setBusy(true);
    setErr('');
    try {
      await onSubmit(val.trim());
      onClose();
    } catch (ex: any) {
      setErr(ex?.message || 'Something went wrong');
      setBusy(false);
    }
  };
  return (
    <div className="fx-modal-backdrop" onClick={onClose}>
      <form className="fx-modal fx-input-modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="fx-login-title">{title}</div>
        <label className="fx-field-label">{label}</label>
        <input
          className="fx-text-input"
          autoFocus
          placeholder={placeholder}
          value={val}
          onChange={(e) => setVal(e.target.value)}
        />
        {err && <div className="fx-login-err">{err}</div>}
        <div className="fx-modal-actions">
          <button type="button" className="fx-login-back" onClick={onClose}>Cancel</button>
          <button type="submit" className="fx-sendbtn" disabled={busy || !val.trim()}>
            {busy ? 'Working…' : submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
}

function AddPeopleModal({
  candidates,
  onAdd,
  onClose,
}: {
  candidates: PublicUser[];
  onAdd: (ids: string[]) => Promise<void>;
  onClose: () => void;
}) {
  const [sel, setSel] = useState<string[]>([]);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const ql = q.trim().toLowerCase();
  const list = candidates.filter((u) => !ql || (u.name || u.email || '').toLowerCase().includes(ql));
  const toggle = (id: string) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const submit = async () => {
    if (!sel.length || busy) return;
    setBusy(true);
    setErr('');
    try {
      await onAdd(sel);
    } catch (ex: any) {
      setErr(ex?.message || 'Could not add people');
      setBusy(false);
    }
  };
  return (
    <div className="fx-modal-backdrop" onClick={onClose}>
      <div className="fx-modal fx-dir" onClick={(e) => e.stopPropagation()}>
        <div className="fx-login-title">Add people</div>
        {candidates.length === 0 ? (
          <div className="fx-empty-small">Everyone in the workspace is already in this channel.</div>
        ) : (
          <>
            <input
              className="fx-text-input"
              autoFocus
              placeholder="Search people…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <div className="fx-agent-pick">
              {list.map((u) => (
                <label key={u.id} className={`fx-agent-pick-row${sel.includes(u.id) ? ' is-sel' : ''}`}>
                  <input type="checkbox" checked={sel.includes(u.id)} onChange={() => toggle(u.id)} />
                  <span
                    className="fx-chhead-av"
                    style={avatarStyle(u)}
                  >
                    {u.avatar_url ? '' : (u.name || u.email || '?')[0]?.toUpperCase()}
                  </span>
                  <span className="fx-dir-name">{u.name || u.email}</span>
                </label>
              ))}
              {list.length === 0 && <div className="fx-empty-small">No matches</div>}
            </div>
          </>
        )}
        {err && <div className="fx-login-err">{err}</div>}
        <div className="fx-modal-actions">
          <button type="button" className="fx-login-back" onClick={onClose}>Cancel</button>
          {candidates.length === 0 ? (
            <button type="button" className="fx-sendbtn" onClick={onClose}>Done</button>
          ) : (
            <button type="button" className="fx-sendbtn" onClick={submit} disabled={busy || !sel.length}>
              {busy ? 'Adding…' : sel.length ? `Add ${sel.length}` : 'Add'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function AddAgentToChannelModal({
  agents,
  onAdd,
  onNewAgent,
  onClose,
}: {
  agents: Agent[];
  onAdd: (agentId: string, canPost: boolean) => Promise<void>;
  onNewAgent: () => void;
  onClose: () => void;
}) {
  const [sel, setSel] = useState(agents[0]?.id || '');
  const [canPost, setCanPost] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const submit = async () => {
    if (!sel || busy) return;
    setBusy(true);
    setErr('');
    try {
      await onAdd(sel, canPost);
    } catch (ex: any) {
      setErr(ex?.message || 'Could not add agent');
      setBusy(false);
    }
  };
  return (
    <div className="fx-modal-backdrop" onClick={onClose}>
      <div className="fx-modal fx-dir" onClick={(e) => e.stopPropagation()}>
        <div className="fx-login-title">Add an agent to this channel</div>
        <div className="fx-agent-pick">
          {agents.map((a) => (
            <label key={a.id} className={`fx-agent-pick-row${sel === a.id ? ' is-sel' : ''}`}>
              <input type="radio" name="agentpick" checked={sel === a.id} onChange={() => setSel(a.id)} />
              <span className="fx-agent-av">🤖</span>
              <span className="fx-dir-name">{a.name}</span>
            </label>
          ))}
        </div>
        <label className="fx-check-row">
          <input type="checkbox" checked={canPost} onChange={(e) => setCanPost(e.target.checked)} />
          <span>Allow this agent to post here (uncheck for read-only)</span>
        </label>
        {err && <div className="fx-login-err">{err}</div>}
        <div className="fx-modal-actions">
          <button type="button" className="fx-login-back" onClick={onNewAgent}>+ New agent</button>
          <button type="button" className="fx-sendbtn" onClick={submit} disabled={busy || !sel}>
            {busy ? 'Adding…' : 'Add to channel'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ChatView({
  conversation,
  messages,
  hasMore,
  loadOlder,
  addLocalEcho,
  confirmLocalEcho,
  failLocalEcho,
  typing,
  agentStatuses,
  pollLive,
  promptLive,
  reads,
  user,
  members,
  agents,
  channels,
  dms,
  callsEnabled,
  workspaceId,
  canManageWorkspace,
  isStarred,
  onToggleStar,
  isMuted,
  onToggleMute,
  onOpenSearch,
  onReloadMembers,
  onReloadChannels,
  onOpenProfile,
  onAddAgentToChannel,
  onViewed,
  onViewScheduled,
  onMarkUnread,
  onOpenConversation,
  onDm,
  onLeaveChannel,
  onDeleteChannel,
  suggestions,
  onSuggestionResolved,
  pendingCall,
  onPendingConsumed,
  onHome,
  onKudos,
  inCall,
  onStartCall,
}: {
  conversation?: Channel & Partial<Dm>;
  messages: Message[];
  hasMore: boolean;
  loadOlder: () => Promise<number>;
  addLocalEcho: (m: Message) => void;
  confirmLocalEcho: (tempId: string, real: Message) => void;
  failLocalEcho: (tempId: string) => void;
  typing: TypingSignal[];
  agentStatuses: AgentStatus[];
  reads: Record<string, string>;
  pollLive: import('./realtime').PollLive;
  promptLive: import('./realtime').PromptLive;
  user: ReturnType<typeof useAuth>['user'];
  members: PublicUser[];
  agents: Agent[];
  channels: Channel[];
  dms: Dm[];
  callsEnabled: boolean;
  workspaceId: string;
  canManageWorkspace?: boolean;
  isStarred: boolean;
  onToggleStar: () => void;
  isMuted: boolean;
  onToggleMute: () => void;
  onOpenSearch: () => void;
  onReloadMembers: () => void;
  onReloadChannels?: () => void;
  onOpenProfile?: (userId: string) => void;
  onAddAgentToChannel: () => void;
  onViewed: (cid: string) => void;
  onViewScheduled: () => void;
  onMarkUnread: (cid: string, at: string) => void;
  onOpenConversation: (cid: string) => void;
  onDm: (type: 'user' | 'agent', id: string) => void;
  onLeaveChannel: (cid: string) => void;
  onDeleteChannel: (cid: string) => void;
  suggestions: HelmSuggestion[];
  onSuggestionResolved: (id: string) => void;
  pendingCall: { convId: string; video: boolean } | null;
  onPendingConsumed: () => void;
  inCall: boolean;
  onStartCall: (c: { convId: string; title: string; video: boolean; initiated: boolean }) => void;
  onHome: () => void;
  onKudos?: () => void;
}) {
  const { patchUser } = useAuth();
  const meId = user?.id;
  const p = user?.prefs;
  const setPref = usePrefSetter(user, patchUser);
  const [draft, setDraft] = useState(() => (conversation ? getDraftText(conversation.id) : ''));
  // AI: pre-send tone preview + channel catch-up (both via the mgmt LLM relay).
  const [tone, setTone] = useState<{ verdict: string; issues: string[]; rewrite: string | null } | null>(null);
  const [toneBusy, setToneBusy] = useState(false);
  const [catchup, setCatchup] = useState<Awaited<ReturnType<typeof api.catchup>> | null>(null);
  const [catchupBusy, setCatchupBusy] = useState(false);
  // Declared BEFORE the routing effect below, which reads it (TDZ crash otherwise).
  const [editing, setEditing] = useState<Message | null>(null);
  // Smart Routing: when the draft is a question, suggest better channels/people (debounced).
  const [route, setRoute] = useState<{ channels: { id: string; name: string; matches: number }[]; people: { id: string; name: string; matches: number }[] } | null>(null);
  useEffect(() => {
    const d = draft.trim();
    // Only volunteer routing when it can plausibly beat the author's own judgment:
    // never while editing an old message, and never when the draft @mentions
    // someone — an addressed question already has its audience right here.
    if (!conversation || editing || !user?.ai_features_enabled || d.includes('@') || !d.endsWith('?') || d.length < 15) { setRoute(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.routingSuggest(d, conversation.id);
        // Weak single-match hits are noise — demand real signal, and never
        // suggest the conversation the user is already in.
        const channels = (r.channels || []).filter((c: any) => c.id !== conversation.id && (c.matches || 0) >= 2).slice(0, 3);
        const people = (r.people || []).filter((pp: any) => (pp.matches || 0) >= 2).slice(0, 2);
        setRoute(r.available && (channels.length || people.length) ? { channels, people } : null);
      } catch { setRoute(null); }
    }, 900);
    return () => clearTimeout(t);
  }, [draft, conversation, editing]);
  // Answer-as-you-type: a question being drafted checks the knowledge base —
  // if an entry answers it, an inline card appears before the message is sent.
  const [kbHint, setKbHint] = useState<import('./api').KbHit | null>(null);
  const [kbHintOpen, setKbHintOpen] = useState(false);
  const kbDismissed = useRef<Set<string>>(new Set());
  useEffect(() => {
    const d = draft.trim();
    if (!conversation || editing || !d.endsWith('?') || d.length < 15) { setKbHint(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.kbAnswer(workspaceId, d);
        setKbHint(r.entry && !kbDismissed.current.has(r.entry.id) ? r.entry : null);
        setKbHintOpen(false);
      } catch { setKbHint(null); }
    }, 900);
    return () => clearTimeout(t);
  }, [draft, conversation, editing, workspaceId]);

  const runToneCheck = async () => {
    const t = draft.trim();
    if (t.length < 15) return;
    setToneBusy(true); setTone(null);
    try {
      const r = await api.toneCheck(t);
      if (r.available) setTone({ verdict: r.verdict || 'clear', issues: r.issues || [], rewrite: r.rewrite ?? null });
      else setTone({ verdict: 'clear', issues: [], rewrite: null });
    } catch { /* silent — optional feature */ }
    finally { setToneBusy(false); }
  };
  const runCatchup = async () => {
    if (!conversation) return;
    setCatchupBusy(true);
    try { setCatchup(await api.catchup(conversation.id)); }
    catch { setCatchup(null); }
    finally { setCatchupBusy(false); }
  };
  const [pending, setPending] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [pickerFor, setPickerFor] = useState<string | null>(null);
  const [menuFor, setMenuFor] = useState<{ id: string; rect: DOMRect } | null>(null);
  // "Seen by" reader list is click-to-reveal, not hover — reset per menu open.
  const [seenListOpen, setSeenListOpen] = useState(false);
  const [fwdMsg, setFwdMsg] = useState<Message | null>(null);
  const [openThreadRoot, setOpenThreadRoot] = useState<string | null>(null);
  useEffect(() => { setOpenThreadRoot(null); }, [conversation?.id]);
  // Forum mode: local override so the toggle flips instantly; also listens for a live
  // channel.mode broadcast (another admin flipping it). Resets when the channel changes.
  const [modeOverride, setModeOverride] = useState<string | null>(null);
  useEffect(() => { setModeOverride(null); }, [conversation?.id]);
  const forumMode = (modeOverride ?? conversation?.mode) === 'forum';
  const [pinsOpen, setPinsOpen] = useState(false);
  const [createPollOpen, setCreatePollOpen] = useState(false);
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [declareIncidentOpen, setDeclareIncidentOpen] = useState(false);
  const [incidentReloadKey, setIncidentReloadKey] = useState(0);
  const [receiptsFor, setReceiptsFor] = useState<string | null>(null);
  const [flash, setFlash] = useState('');
  // Channel header: tabs + member list + notification/kebab popovers
  const [tab, setTab] = useState<'messages' | 'canvas' | 'files' | 'docs'>('messages');
  const [chMembers, setChMembers] = useState<ChannelMembers | null>(null);
  const [membersOpen, setMembersOpen] = useState(false);
  const [peopleOpen, setPeopleOpen] = useState(false);
  const [memberMenu, setMemberMenu] = useState<
    { kind: 'user' | 'agent'; id: string; name: string; rect: DOMRect } | null
  >(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const [headMenuOpen, setHeadMenuOpen] = useState(false);
  useOutsideTapClose(membersOpen, () => setMembersOpen(false), '.fx-head-pop, .fx-chhead');
  // The reaction emoji picker must dismiss on any outside click/tap.
  useOutsideTapClose(!!pickerFor, () => setPickerFor(null), '.fx-react-picker, .fx-msgbar');
  useOutsideTapClose(notifOpen, () => setNotifOpen(false), '.fx-head-pop, .fx-chhead');
  useOutsideTapClose(headMenuOpen, () => setHeadMenuOpen(false), '.fx-head-pop, .fx-chhead');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsTab, setDetailsTab] = useState<'about' | 'members' | 'settings'>('about');
  const openDetails = (t: 'about' | 'members' | 'settings' = 'about') => { setDetailsTab(t); setDetailsOpen(true); };
  const [recording, setRecording] = useState(false);
  const [clipRecording, setClipRecording] = useState(false);
  const [findTimeOpen, setFindTimeOpen] = useState(false);
  // Touch devices: which message's action bar is open (tap-toggled; hover doesn't exist).
  const [tapMsg, setTapMsg] = useState<string | null>(null);
  const recRef = useRef<MediaRecorder | null>(null);
  const clipRef = useRef<ClipSession | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const dividerRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const lastTyping = useRef(0);

  // The composer floats over the timeline and its height changes (formatting bar,
  // attachments, multi-line). Publish its real height as --composer-h so the timeline's
  // bottom padding + the typing/autocomplete offsets track it — otherwise the last
  // message and the "typing…" note slip behind the taller composer.
  useEffect(() => {
    const chat = scrollRef.current?.parentElement as HTMLElement | null;
    if (!chat) return undefined;
    const composer = chat.querySelector('.fx-composer') as HTMLElement | null;
    if (!composer) { chat.style.removeProperty('--composer-h'); return undefined; }
    const sync = () => chat.style.setProperty('--composer-h', `${composer.offsetHeight}px`);
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(composer);
    return () => ro.disconnect();
  }, [conversation?.id, tab]);

  // Header popups (members / notify / more) close on any interaction outside them —
  // e.g. opening search — not only on mouse-leave, so one never lingers behind other UI.
  useEffect(() => {
    if (!membersOpen && !notifOpen && !headMenuOpen) return undefined;
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.closest('.fx-head-pop') || t.closest('.fx-headpop-toggle'))) return;
      setMembersOpen(false); setNotifOpen(false); setHeadMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [membersOpen, notifOpen, headMenuOpen]);
  // Snapshot the read cursor at open so the "New" divider stays put while viewing.
  const [openedReadAt] = useState<string | null>(conversation?.last_read_at || null);

  useEffect(() => {
    if (conversation) onViewed(conversation.id);
    setEditing(null);
    setTab('messages');
    setMembersOpen(false);
    setNotifOpen(false);
    setHeadMenuOpen(false);
    setChMembers(null);
    if (conversation) {
      let alive = true;
      api.channelMembers(conversation.id).then((m) => alive && setChMembers(m)).catch(() => {});
      return () => { alive = false; };
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation?.id]);
  const refreshMembers = () => {
    if (conversation) api.channelMembers(conversation.id).then(setChMembers).catch(() => {});
  };
  // Ongoing-call awareness: poll LiveKit (via the API) while this conversation is
  // open, so members who missed the ring see a "call in progress — Join" banner.
  const [liveCall, setLiveCall] = useState<{ active: boolean; count: number }>({ active: false, count: 0 });
  useEffect(() => {
    setLiveCall({ active: false, count: 0 });
    if (!conversation || !callsEnabled) return undefined;
    let alive = true;
    const check = () => api.callStatus(conversation.id).then((s) => { if (alive) setLiveCall(s); }).catch(() => {});
    check();
    // LiveKit needs a few seconds to drop a just-departed participant — the
    // early re-check kills the stale "1 in the call" banner right after a
    // leave instead of parking it until the next poll tick.
    const settle = window.setTimeout(check, 4000);
    const t = window.setInterval(check, 8000);
    return () => { alive = false; window.clearTimeout(settle); window.clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation?.id, callsEnabled, inCall]);
  // Live member-list updates: the server publishes members.changed on every
  // add/remove (people and agents), so viewers — including the adder — refetch
  // without leaving the channel.
  useEffect(() => {
    const onChanged = (e: Event) => {
      if ((e as CustomEvent).detail?.channelId === conversation?.id) refreshMembers();
    };
    window.addEventListener('fx:members-changed', onChanged);
    return () => window.removeEventListener('fx:members-changed', onChanged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation?.id]);

  // Auto-start the call when we accept an incoming ring for this conversation.
  useEffect(() => {
    if (pendingCall && conversation && pendingCall.convId === conversation.id) {
      onStartCall({ convId: conversation.id, title: conversation.name || 'call', video: pendingCall.video, initiated: false });
      onPendingConsumed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingCall, conversation?.id]);

  const readMode = getPref(p, 'mark_read.mode', 'left_off_mark');
  const startLeftOff = readMode === 'left_off_mark';
  useEffect(() => {
    if (startLeftOff && dividerRef.current) {
      dividerRef.current.scrollIntoView({ block: 'center' });
    } else {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length > 0, conversation?.id]);

  const [showJump, setShowJump] = useState(false);
  const lastMsgIdRef = useRef<string | null>(null);
  const prependingRef = useRef(false);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // "New" means the LAST MESSAGE ID changed — reactions/edits/typing also
    // re-run this effect and were lighting the pill with nothing new to read.
    const last = messages[messages.length - 1];
    const isNew = (last?.id || null) !== lastMsgIdRef.current;
    lastMsgIdRef.current = last?.id || null;
    if (prependingRef.current) return; // older history arriving — never yank to bottom
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 240) {
      el.scrollTop = el.scrollHeight;
    } else if (isNew && last && !(last.sender_type === 'user' && last.sender_id === meId)) {
      // Reader is scrolled up in history — don't yank them down; offer a jump.
      setShowJump(true);
    }
  }, [messages, typing]);
  useEffect(() => { setShowJump(false); lastMsgIdRef.current = null; }, [conversation?.id]);
  const jumpToLatest = () => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    setShowJump(false);
  };

  if (!conversation) {
    return (
      <main className="fx-chat fx-main--empty">
        <div className="fx-empty-big">Select a channel or DM, or create one to start chatting.</div>
      </main>
    );
  }

  const isDm = conversation.kind === 'dm';
  const title = isDm ? conversation.name || '' : `# ${conversation.name}`;
  // Time Zone Intelligence: the DM partner's current local time ("· 3:14 PM local" or
  // "· 11:40 PM · off hours"), computed from their IANA tz + working hours.
  const dmHint = isDm && conversation.dm_with ? localTimeHint(conversation.dm_with) : null;

  const senderName = (m: Message): string => {
    if (m.sender_id === meId) return 'You';
    // Prefer the server-resolved name; fall back to the local member/agent list.
    const full =
      m.sender_name ||
      (m.sender_type === 'agent' ? agents.find((a) => a.id === m.sender_id)?.name : undefined) ||
      members.find((mm) => mm.id === m.sender_id)?.name ||
      members.find((mm) => mm.id === m.sender_id)?.email ||
      (m.sender_type === 'agent' ? 'Agent' : 'Member');
    return getPref(p, 'messages.names', 'full') === 'display' ? full.split(' ')[0] : full;
  };
  const senderAvatar = (m: Message): React.CSSProperties => {
    const style: Record<string, string | number> = { '--h': hueOf(m.sender_id) };
    if (m.sender_type === 'agent') return style as React.CSSProperties;
    const url = m.sender_avatar || members.find((mm) => mm.id === m.sender_id)?.avatar_url;
    if (url) style.backgroundImage = `url(${url})`;
    return style as React.CSSProperties;
  };

  const notifyTyping = () => {
    if (getPref(p, 'privacy.hide_typing', false)) return;
    const now = Date.now();
    if (now - lastTyping.current > 2000) {
      lastTyping.current = now;
      api.sendTyping(conversation.id).catch(() => {});
    }
  };
  const pickFiles = () => fileRef.current?.click();
  // Shared upload path for the file button, paste, and drag-drop. `confirm`
  // gates the external-files prompt (skipped for paste/drop — the gesture is
  // already deliberate) so the same alt-text + pending logic serves all three.
  const uploadFiles = async (files: File[], confirm = true) => {
    if (!files.length) return;
    if (
      confirm && getPref(p, 'advanced.confirm_external_files', true) &&
      !window.confirm(`Upload ${files.length} file${files.length > 1 ? 's' : ''} to this conversation?`)
    ) {
      return;
    }
    setUploading(true);
    try {
      for (const file of files) {
        const att = await api.uploadFile(file);
        if (getPref(p, 'accessibility.alt_text_reminder', false) && file.type.startsWith('image/')) {
          const alt = window.prompt(`Describe this image for screen readers:\n${file.name}`);
          if (alt?.trim()) att.alt = alt.trim();
        }
        setPending((cur) => [...cur, att]);
      }
    } catch (ex: any) {
      window.alert(ex?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };
  const onFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    await uploadFiles(files);
  };
  const send = async () => {
    let body = draft.trim();
    if (!body && pending.length === 0) return;
    if (getPref(p, 'messages.convert_emoticons', true)) body = convertEmoticons(body);
    if (editing) {
      const target = editing;
      setEditing(null);
      setDraft('');
      saveDraft(conversation.id, '');
      try {
        await api.editMessage(target.id, body);
      } catch (ex: any) {
        window.alert(ex?.message || 'Could not edit');
        setDraft(body);
        setEditing(target);
      }
      return;
    }
    const atts = pending;
    setDraft('');
    saveDraft(conversation.id, '');
    setPending([]);
    // Optimistic echo: the message appears instantly; the server row replaces it
    // when the POST resolves (or the realtime echo arrives first — both dedupe).
    const tempId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    if (!atts.length) {
      addLocalEcho({
        id: tempId, channel_id: conversation.id, sender_type: 'user', sender_id: meId || '',
        sender_name: user?.name || user?.email, body, created_at: new Date().toISOString(), pending: true,
      } as Message);
      requestAnimationFrame(() => {
        const el = scrollRef.current;
        if (el) el.scrollTop = el.scrollHeight;
      });
    }
    try {
      const real = atts.length
        ? await api.sendMessageWith(conversation.id, body, atts)
        : await api.sendMessage(conversation.id, body);
      if (!atts.length && real?.id) confirmLocalEcho(tempId, real as Message);
    } catch (ex: any) {
      if (!atts.length) failLocalEcho(tempId);
      window.alert(ex?.message || 'Could not send the message');
      setDraft(body);
      setPending(atts);
    }
  };
  const startEditLast = () => {
    const mine = [...messages].reverse().find((m) => m.sender_type === 'user' && m.sender_id === meId);
    if (!mine) return;
    setEditing(mine);
    setDraft(mine.body);
  };
  const cancelEdit = () => {
    setEditing(null);
    setDraft('');
    saveDraft(conversation.id, '');
  };
  const focusList = () => {
    const el = scrollRef.current;
    if (el) {
      el.focus();
      el.scrollTop = el.scrollHeight;
    }
  };
  const schedule = async (iso: string) => {
    let body = draft.trim();
    if (!body && pending.length === 0) return;
    if (getPref(p, 'messages.convert_emoticons', true)) body = convertEmoticons(body);
    try {
      await api.scheduleMessage(conversation.id, body, pending, iso);
      setDraft('');
      saveDraft(conversation.id, '');
      setPending([]);
      window.alert(`Scheduled ✓ — sends ${new Date(iso).toLocaleString()}`);
    } catch (ex: any) {
      window.alert(ex?.message || 'Could not schedule');
    }
  };
  const onCommand = async (cmd: string, arg: string) => {
    try {
      if (cmd === '/away') patchUser(await api.updatePresence('away'));
      else if (cmd === '/active') patchUser(await api.updatePresence('active'));
      else if (cmd === '/call') startCall(true);
      else if (cmd === '/status') {
        const parts = arg.trim().split(/\s+/);
        const first = parts[0] || '';
        const isEmoji = first && /\p{Extended_Pictographic}/u.test(first);
        patchUser(
          await api.updateProfile({
            status_emoji: isEmoji ? first : '💬',
            status_text: (isEmoji ? parts.slice(1).join(' ') : arg.trim()) || undefined,
          }),
        );
      }
      else if (cmd === '/catchup') runCatchup();
      else if (cmd === '/tone') runToneCheck();
      else if (cmd === '/poll') setCreatePollOpen(true);
      else if (cmd === '/checklist') setChecklistOpen(true);
      else if (cmd === '/incident') setDeclareIncidentOpen(true);
      else if (cmd === '/findtime') setFindTimeOpen(true);
      else if (cmd === '/canvas') setTab('canvas');
      else if (cmd === '/invite') setPeopleOpen(true);
      else if (cmd === '/kudos') onKudos?.();
    } catch { /* best-effort */ }
  };
  const toggleRecord = async () => {
    if (recording) {
      recRef.current?.stop();
      return;
    }
    try {
      const micId = getPref(p, 'av.mic_id', '');
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: micId ? { deviceId: micId } : true,
      });
      const rec = new MediaRecorder(stream);
      const chunks: Blob[] = [];
      let startedAt = 0;
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunks, { type: rec.mimeType || 'audio/webm' });
        if (blob.size < 200) return;
        const durationMs = startedAt ? Date.now() - startedAt : undefined;
        setUploading(true);
        try {
          const file = new File([blob], `voice-note-${Date.now()}.webm`, { type: blob.type });
          const att = await api.uploadFile(file);
          // kind + duration light up the inline player; the audio/* mime also triggers
          // the server's Whisper transcription (see transcribe.py).
          setPending((cur) => [...cur, { ...att, kind: 'audio', duration_ms: durationMs }]);
        } catch (ex: any) {
          window.alert(ex?.message || 'Voice note upload failed');
        } finally {
          setUploading(false);
        }
      };
      recRef.current = rec;
      startedAt = Date.now();
      rec.start();
      setRecording(true);
    } catch {
      window.alert('Microphone access was denied.');
    }
  };
  const finishClip = async () => {
    const sess = clipRef.current;
    if (!sess) return;
    clipRef.current = null;
    setClipRecording(false);
    const { blob, durationMs, width, height } = await sess.stop();
    if (blob.size < 1000) return;
    setUploading(true);
    try {
      const file = new File([blob], `clip-${Date.now()}.webm`, { type: blob.type });
      const att = await api.uploadFile(file);
      // kind:'video' + dims light up the inline player; the video/* mime also triggers
      // the server's Whisper transcription (see transcribe.py).
      setPending((cur) => [...cur, { ...att, kind: 'video', duration_ms: durationMs, width, height }]);
    } catch (ex: any) {
      window.alert(ex?.message || 'Clip upload failed');
    } finally {
      setUploading(false);
    }
  };
  const toggleClip = async () => {
    if (clipRecording) {
      await finishClip();
      return;
    }
    try {
      const camId = getPref(p, 'av.camera_id', '');
      const micId = getPref(p, 'av.mic_id', '');
      const sess = await startScreenCameraClip(camId, micId);
      sess.onScreenEnded(() => { finishClip(); });
      clipRef.current = sess;
      setClipRecording(true);
    } catch {
      window.alert('Screen capture was denied.');
    }
  };
  const startCall = (video = true) => {
    if (!callsEnabled) {
      window.alert('Calls light up once LiveKit is configured (server .env → LIVEKIT_*).');
      return;
    }
    const threshold = getPref(p, 'av.large_channel_threshold', 150);
    if (
      !isDm &&
      getPref(p, 'av.warn_large_channel', true) &&
      members.length > threshold &&
      !window.confirm(`This channel reaches ${members.length} people. Start the call anyway?`)
    ) {
      return;
    }
    if (!conversation) return;
    onStartCall({ convId: conversation.id, title: conversation.name || 'call', video, initiated: true });
  };
  const ringAndStart = async (memberIds: string[], video: boolean) => {
    if (!conversation) return;
    try { await api.ringCall(conversation.id, memberIds, video); } catch { /* best-effort */ }
    startCall(video);
  };
  const onVideo = () => startCall(true);
  const toggleReaction = async (m: Message, emoji: string) => {
    setPickerFor(null);
    recordEmojiUse(emoji);
    const mine = (m.reactions || []).find((r) => r.emoji === emoji)?.actors.includes(`user:${meId}`);
    try {
      if (mine) await api.removeReaction(m.id, emoji);
      else await api.addReaction(m.id, emoji);
    } catch { /* realtime reconciles */ }
  };
  const toast = (t: string) => {
    setFlash(t);
    window.setTimeout(() => setFlash(''), 1600);
  };
  const openMenu = (m: Message, e: React.MouseEvent) => {
    e.stopPropagation();
    setSeenListOpen(false);
    setMenuFor(menuFor?.id === m.id ? null : { id: m.id, rect: e.currentTarget.getBoundingClientRect() });
  };
  const editThis = (m: Message) => {
    setMenuFor(null);
    setEditing(m);
    setDraft(m.body);
  };
  const copyLink = async (m: Message) => {
    setMenuFor(null);
    const url = `${location.origin}${location.pathname}?c=${m.channel_id}&m=${m.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast('Link copied');
    } catch {
      window.prompt('Copy message link:', url);
    }
  };
  const togglePin = async (m: Message) => {
    setMenuFor(null);
    try {
      await api.pinMessage(m.id);
      toast(m.pinned ? 'Unpinned' : 'Pinned to channel');
    } catch (ex: any) {
      window.alert(ex?.message || 'Could not pin');
    }
  };
  const removeMessage = async (m: Message) => {
    setMenuFor(null);
    if (!window.confirm('Delete this message? This can’t be undone.')) return;
    try {
      await api.deleteMessage(m.id);
    } catch (ex: any) {
      window.alert(ex?.message || 'Could not delete');
    }
  };
  const forwardTo = async (targetId: string) => {
    const src = fwdMsg;
    setFwdMsg(null);
    if (!src) return;
    const quoted = (src.body || '').split('\n').map((l) => `> ${l}`).join('\n');
    const body = `_Forwarded from ${title} · ${senderName(src)}_\n${quoted}`.trim();
    try {
      await api.sendMessage(targetId, body);
      toast('Forwarded');
    } catch (ex: any) {
      window.alert(ex?.message || 'Could not forward');
    }
  };
  const pinnedCount = messages.filter((m) => m.pinned).length;
  const copyConvLink = async () => {
    const url = `${location.origin}${location.pathname}?c=${conversation.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast('Link copied');
    } catch {
      window.prompt('Copy link:', url);
    }
  };
  const isPrivate = conversation.kind === 'private';
  const myRole = members.find((m) => m.id === meId)?.role;
  const isAdmin = myRole === 'owner' || myRole === 'admin';
  const canManageChannel = isAdmin || !!canManageWorkspace;
  const toggleForumMode = async () => {
    if (!conversation) return;
    const next = forumMode ? 'chat' : 'forum';
    try {
      await api.setChannelMode(conversation.id, next);
      setModeOverride(next);
      onReloadChannels?.();
    } catch (e: any) { toast(e?.message || 'Could not change mode.'); }
  };
  const postForumTopic = async (topicTitle: string, topicBody: string) => {
    if (!conversation) return;
    await api.sendMessage(conversation.id, topicBody, { title: topicTitle });
  };
  // Read receipts: show "Seen" under my most-recent message once others have read past it.
  const myLastMsgId = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const mm = messages[i];
      if (mm.sender_type === 'user' && mm.sender_id === meId) return mm.id;
    }
    return null;
  })();
  // Read receipts: the chat surface only shows a quiet eye
  // when EVERYONE has seen a message; the full "Seen by" list lives in the
  // message's ⋯ menu (hover to expand).
  const seenByFor = (m: Message): string[] => {
    if (!m.created_at) return [];
    const t = Date.parse(m.created_at);
    return Object.entries(reads)
      .filter(([uid, at]) => uid !== meId && uid !== m.sender_id && Date.parse(at) >= t)
      .map(([uid]) => members.find((x) => x.id === uid)?.name || 'Someone');
  };
  const seenByEveryone = (m: Message): boolean => {
    if (!m.created_at) return false;
    const others = (chMembers?.users || []).filter((u) => u.id !== meId && u.id !== m.sender_id);
    if (!others.length) return false;
    const t = Date.parse(m.created_at);
    return others.every((u) => reads[u.id] && Date.parse(reads[u.id]) >= t);
  };
  const openMemberMenu = (kind: 'user' | 'agent', id: string, name: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setMemberMenu({ kind, id, name, rect: e.currentTarget.getBoundingClientRect() });
  };
  const doRemoveFromChannel = async (userId: string) => {
    setMemberMenu(null);
    try {
      await api.removeChannelPerson(conversation.id, userId);
      refreshMembers();
      toast('Removed from channel');
    } catch (ex: any) {
      window.alert(ex?.message || 'Could not remove');
    }
  };
  const doRemoveAgent = async (agentId: string) => {
    setMemberMenu(null);
    try {
      await api.removeAgentFromChannel(conversation.id, agentId);
      refreshMembers();
      toast('Agent removed from channel');
    } catch (ex: any) {
      window.alert(ex?.message || 'Could not remove');
    }
  };
  const doRemoveFromWorkspace = async (userId: string) => {
    setMemberMenu(null);
    try {
      await api.removeWorkspaceMember(workspaceId, userId);
      refreshMembers();
      onReloadMembers();
      toast('Removed from workspace');
    } catch (ex: any) {
      window.alert(ex?.message || 'Could not remove');
    }
  };

  const showTyping = getPref(p, 'messages.show_typing', true);
  const typingNames = showTyping ? typing.filter((t) => t.actor !== `user:${meId}`).map((t) => t.name) : [];
  const quick: string[] = quickReactions(p);
  const oneClick = getPref(p, 'messages.one_click_reactions', true);
  const inlineMedia = getPref(p, 'messages.inline_media', true);
  const emojiPlain = getPref(p, 'messages.emoji_plain', false);
  const jumbo = getPref(p, 'messages.jumbomoji', true) && !emojiPlain;
  const alwaysTs = getPref(p, 'appearance.show_timestamps_always', false);
  const mdOpts = {
    colorSwatches: getPref(p, 'messages.color_swatches', true),
    emojiPlain,
    linkMedia: getPref(p, 'messages.link_media', true),
    channels,
    // Agents are mentionable too — carry them with a marked id so a click on
    // "@Beetle" opens the agent DM instead of a (nonexistent) user DM.
    members: [...members, ...agents.map((a) => ({ id: `agent:${a.id}`, name: a.name }))],
    onChannel: (id: string) => onOpenConversation(id),
    onMention: (id: string) => (id.startsWith('agent:') ? onDm('agent', id.slice(6)) : onDm('user', id)),
  };

  // Unread divider position: first message from someone else after the open-time cursor.
  const dividerBefore = openedReadAt
    ? messages.find((m) => m.sender_id !== meId && (m.created_at || '') > openedReadAt)?.id
    : undefined;

  return (
    <main className={`fx-chat${openThreadRoot ? ' has-thread' : ''}`}>
      <header className="fx-chhead">
        <div className="fx-chhead-row">
          <button
            type="button"
            className="fx-chhead-back"
            title="Back to home"
            onClick={onHome}
          >
            <Icon name="home" size={17} />
          </button>
          <button
            type="button"
            className={`fx-chhead-star${isStarred ? ' is-on' : ''}`}
            title={isStarred ? 'Starred' : 'Star this conversation'}
            onClick={onToggleStar}
          >
            <Icon name="star" size={18} />
          </button>
          <div className="fx-chhead-name">
            {isDm && conversation.dm_with && (
              <span className={`fx-presence-dot${conversation.dm_with.presence === 'away' ? ' is-away' : ''}`} />
            )}
            {isDm ? (
              <span className="fx-chhead-title">{title}</span>
            ) : (
              <button type="button" className="fx-chhead-title fx-chhead-titlebtn" title="Channel details" onClick={() => openDetails('about')}>
                {title}
              </button>
            )}
            {isDm && conversation.dm_with?.status_emoji && (
              <span className="fx-chhead-emoji">{conversation.dm_with.status_emoji}</span>
            )}
            {dmHint && (
              <span className={`fx-chhead-localtime${/off hours/.test(dmHint) ? ' is-off' : ''}`} title="Their local time">
                · {dmHint}
              </span>
            )}
            {isMuted && <span className="fx-chhead-muted" title="Muted"><Icon name="bell" size={12} /></span>}
          </div>
          {!isDm && chMembers && chMembers.count > 0 && (
            <button
              type="button"
              className="fx-chhead-members fx-headpop-toggle"
              title="Members"
              onClick={() => { setMembersOpen((v) => !v); setNotifOpen(false); setHeadMenuOpen(false); }}
            >
              <span className="fx-chhead-avs">
                {chMembers.users.slice(0, 3).map((u) => (
                  <span
                    key={u.id}
                    className="fx-chhead-av"
                    style={avatarStyle(u)}
                  >
                    {u.avatar_url ? '' : (u.name || u.email || '?')[0]?.toUpperCase()}
                  </span>
                ))}
              </span>
              <span className="fx-chhead-count">{chMembers.count}</span>
            </button>
          )}
          <div className="fx-chhead-spacer" />
          <div className="fx-head-actions">
            {callsEnabled && !inCall && (
              <CallLauncher
                kind={isDm ? 'dm' : 'channel'}
                /* Ring the CONVERSATION's participants, not the whole workspace
                   roster (`members`) — that made a DM call ring everyone. */
                members={chMembers?.users?.length ? chMembers.users : members}
                meId={meId}
                onStart={startCall}
                onRing={ringAndStart}
                className="fx-call-head-launcher"
              />
            )}
            <button
              type="button"
              className="fx-ic-head"
              title="Find a time to meet"
              onClick={() => setFindTimeOpen(true)}
            >
              <Icon name="clock" size={17} />
            </button>
            <button
              type="button"
              className={`fx-ic-head fx-headpop-toggle${isMuted || channelNotifyLevel(p, conversation?.id) === 'nothing' ? ' is-on' : ''}`}
              title="Notification preferences"
              onClick={() => { setNotifOpen((v) => !v); setMembersOpen(false); setHeadMenuOpen(false); }}
            >
              <Icon name="bell" size={17} />
            </button>
            <button type="button" className="fx-ic-head" title="Search" onClick={onOpenSearch}>
              <Icon name="search" size={17} />
            </button>
            <button
              type="button"
              className="fx-ic-head fx-headpop-toggle"
              title="More"
              onClick={() => { setHeadMenuOpen((v) => !v); setMembersOpen(false); setNotifOpen(false); }}
            >
              <Icon name="more" size={17} />
            </button>
          </div>
        </div>
        {!isDm && 'topic' in conversation && conversation.topic && (
          <div className="fx-chat-topic">{conversation.topic}</div>
        )}
        <nav className="fx-chan-tabs">
          <button type="button" className={`fx-chan-tab${tab === 'messages' ? ' is-active' : ''}`} onClick={() => setTab('messages')}>
            <Icon name="dm" size={14} /> Messages
          </button>
          {/* Vault tab removed per Matt — the canvas view code is kept, just not surfaced. */}
          <button type="button" className={`fx-chan-tab${tab === 'files' ? ' is-active' : ''}`} onClick={() => setTab('files')}>
            <Icon name="files" size={14} /> Files &amp; links
          </button>
        </nav>

        {membersOpen && chMembers && (
          <div className="fx-head-pop fx-members-pop" onMouseLeave={() => setMembersOpen(false)}>
            <div className="fx-head-pop-title">{chMembers.count} in this {isDm ? 'conversation' : 'channel'}</div>
            <div className="fx-head-pop-list">
              {chMembers.users.map((u) => {
                const canAct = u.id !== meId; // everyone can at least Message; remove-actions gated in the menu
                return (
                  <div key={u.id} className="fx-memrow">
                    <span
                      className="fx-chhead-av"
                      style={avatarStyle(u)}
                    >
                      {u.avatar_url ? '' : (u.name || u.email || '?')[0]?.toUpperCase()}
                    </span>
                    <span className="fx-memrow-name">{u.name || u.email}{u.id === meId ? ' (you)' : ''}</span>
                    {u.role === 'guest' && <span className="fx-memrow-tag fx-tag-guest">guest</span>}
                    <span className={`fx-presence-dot${u.presence === 'away' ? ' is-away' : ''}`} />
                    {canAct && (
                      <button
                        type="button"
                        className="fx-memrow-x"
                        title="Manage member"
                        onClick={(e) => openMemberMenu('user', u.id, u.name || u.email || 'member', e)}
                      >
                        <Icon name="more" size={14} />
                      </button>
                    )}
                  </div>
                );
              })}
              {chMembers.agents.map((a) => (
                <div key={a.id} className="fx-memrow">
                  <span className="fx-chhead-av">🤖</span>
                  <span className="fx-memrow-name">{a.name}</span>
                  <span className="fx-memrow-tag">agent</span>
                  <button
                    type="button"
                    className="fx-memrow-x"
                    title="Manage agent"
                    onClick={(e) => openMemberMenu('agent', a.id, a.name, e)}
                  >
                    <Icon name="more" size={14} />
                  </button>
                </div>
              ))}
            </div>
            {!isDm && (
              <>
                <button type="button" className="fx-memrow fx-memrow-add" onClick={() => { setMembersOpen(false); setPeopleOpen(true); }}>
                  <span className="fx-chhead-av">＋</span>
                  <span className="fx-memrow-name">Add people…</span>
                </button>
                <button type="button" className="fx-memrow fx-memrow-add" onClick={() => { setMembersOpen(false); onAddAgentToChannel(); }}>
                  <span className="fx-chhead-av">🤖</span>
                  <span className="fx-memrow-name">Add an agent…</span>
                </button>
              </>
            )}
          </div>
        )}
        {notifOpen && conversation && (
          <div className="fx-head-pop fx-notif-pop" onMouseLeave={() => setNotifOpen(false)}>
            <div className="fx-head-pop-title">Notify me about…</div>
            {([
              ['', 'Default (global settings)'],
              ['all', 'All new messages'],
              ['mentions', 'Mentions & keywords only'],
              ['nothing', 'Nothing'],
            ] as const).map(([lvl, label]) => {
              const active = channelNotifyLevel(p, conversation.id) === lvl;
              return (
                <button
                  key={lvl || 'default'}
                  type="button"
                  className={`fx-acct-item fx-notif-opt${active ? ' is-on' : ''}`}
                  onClick={() => { setPref(`notifications.channel_levels.${conversation.id}`, lvl); setNotifOpen(false); }}
                >
                  <span className="fx-notif-check">{active && <Icon name="markread" size={14} />}</span>
                  <span>{label}</span>
                </button>
              );
            })}
            <div className="fx-head-pop-sep" />
            <button type="button" className="fx-acct-item" onClick={() => { onToggleMute(); setNotifOpen(false); }}>
              <span className="fx-notif-check" /><Icon name="bell" size={15} /> {isMuted ? 'Unmute conversation' : 'Mute conversation'}
            </button>
            <div className="fx-head-pop-note">
              {channelNotifyLevel(p, conversation.id) === 'nothing'
                ? 'Silenced — no sounds or alerts here.'
                : channelNotifyLevel(p, conversation.id) === 'mentions'
                ? 'Only @mentions and your keywords will alert.'
                : channelNotifyLevel(p, conversation.id) === 'all'
                ? 'Every message here will alert you.'
                : isMuted ? 'Muted — no unread badges or sounds.' : 'Following your global notification preferences.'}
            </div>
          </div>
        )}
        {headMenuOpen && (
          <div className="fx-head-pop fx-headmenu-pop" onMouseLeave={() => setHeadMenuOpen(false)}>
            <button type="button" className="fx-acct-item" onClick={() => { copyConvLink(); setHeadMenuOpen(false); }}>
              <Icon name="link" size={15} /> Copy link
            </button>
            {pinnedCount > 0 && (
              <button type="button" className="fx-acct-item" onClick={() => { setPinsOpen(true); setHeadMenuOpen(false); }}>
                <Icon name="pin" size={15} /> Pinned ({pinnedCount})
              </button>
            )}
            <button type="button" className="fx-acct-item" onClick={() => { setTab('canvas'); setHeadMenuOpen(false); }}>
              <Icon name="compose" size={15} /> Open vault
            </button>
            <button type="button" className="fx-acct-item" onClick={() => { setCreatePollOpen(true); setHeadMenuOpen(false); }}>
              <Icon name="activity" size={15} /> New poll
            </button>
            <button type="button" className="fx-acct-item" onClick={() => { setChecklistOpen(true); setHeadMenuOpen(false); }}>
              <Icon name="markread" size={15} /> Checklist
            </button>
            {!isDm && (
              <button type="button" className="fx-acct-item" onClick={() => { setHeadMenuOpen(false); setPeopleOpen(true); }}>
                <Icon name="dm" size={15} /> Add people to channel
              </button>
            )}
            {!isDm && (
              <button type="button" className="fx-acct-item" onClick={() => { setHeadMenuOpen(false); onAddAgentToChannel(); }}>
                🤖 Add agent to channel
              </button>
            )}
            {/* Incidents + Forum mode hidden from the frontend for now. */}
            {!isDm && (
              <button type="button" className="fx-acct-item" onClick={() => { setHeadMenuOpen(false); openDetails('settings'); }}>
                <Icon name="clock" size={15} /> Recurring prompts &amp; rules
              </button>
            )}
            <button type="button" className="fx-acct-item" onClick={() => { onToggleMute(); setHeadMenuOpen(false); }}>
              <Icon name="bell" size={15} /> {isMuted ? 'Unmute' : 'Mute'}
            </button>
          </div>
        )}
      </header>

      {findTimeOpen && (
        <FindTimeModal
          workspaceId={workspaceId}
          members={members}
          defaultIds={members.slice(0, 8).map((m) => m.id)}
          onClose={() => setFindTimeOpen(false)}
        />
      )}
      {liveCall.active && !inCall && (
        <div className="fx-livecall-bar" role="status">
          <span className="fx-livecall-dot" aria-hidden />
          A call is happening — {liveCall.count} in the call
          <button
            type="button"
            className="fx-sendbtn"
            style={{ marginLeft: 'auto' }}
            onClick={() => onStartCall({ convId: conversation.id, title: conversation.name || 'call', video: false, initiated: false })}
          >
            Join call
          </button>
        </div>
      )}
      {tab === 'messages' && (
        forumMode ? (
          <ForumBoard
            messages={messages}
            meId={meId}
            members={members}
            channelName={title}
            canPost
            onOpenTopic={(id: string) => setOpenThreadRoot(id)}
            onNewTopic={postForumTopic}
          />
        ) : (
        <>
      {!isDm && conversation && (
        <IncidentBanner
          channelId={conversation.id}
          workspaceId={workspaceId}
          meId={meId}
          members={members}
          reloadKey={incidentReloadKey}
          onToast={toast}
        />
      )}
      {showJump && (
        <button type="button" className="fx-jumpnew" onClick={jumpToLatest}>
          ↓ New messages
        </button>
      )}
      <div className="fx-tl" ref={scrollRef} tabIndex={-1}
        onScroll={(e) => {
          const el = e.currentTarget;
          if (showJump && el.scrollHeight - el.scrollTop - el.clientHeight < 80) setShowJump(false);
          // Near the top with more history on the server → pull the previous
          // page and keep the viewport anchored on what the reader was seeing.
          if (el.scrollTop < 80 && hasMore && !prependingRef.current) {
            prependingRef.current = true;
            const prevH = el.scrollHeight;
            const prevTop = el.scrollTop;
            loadOlder().then((n) => {
              if (n > 0) {
                requestAnimationFrame(() => {
                  el.scrollTop = el.scrollHeight - prevH + prevTop;
                  prependingRef.current = false;
                });
              } else {
                prependingRef.current = false;
              }
            }).catch(() => { prependingRef.current = false; });
          }
        }}>
        {messages.length >= 3 && user?.ai_features_enabled && (
          <div className="fx-catchup">
            {!catchup && (
              <button type="button" className="fx-catchup-cta" onClick={runCatchup} disabled={catchupBusy}>
                <Icon name="bolt" size={14} /> {catchupBusy ? 'Catching you up…' : 'Catch me up'}
              </button>
            )}
            {catchup && catchup.summary && (
              <div className="fx-catchup-card">
                <div className="fx-catchup-head">
                  <span><Icon name="bolt" size={14} /> Catch-up · {catchup.count} messages</span>
                  <button type="button" className="fx-catchup-x" onClick={() => setCatchup(null)} title="Dismiss">×</button>
                </div>
                {catchup.summary.tldr && <p className="fx-catchup-tldr">{catchup.summary.tldr}</p>}
                {catchup.summary.decisions.length > 0 && (
                  <div className="fx-catchup-sec"><b>Decisions</b><ul>{catchup.summary.decisions.map((d, i) => <li key={i}>{d}</li>)}</ul></div>
                )}
                {catchup.summary.action_items.length > 0 && (
                  <div className="fx-catchup-sec"><b>Action items</b><ul>{catchup.summary.action_items.map((a, i) => <li key={i}>{a.who ? <strong>{a.who}: </strong> : null}{a.what}</li>)}</ul></div>
                )}
                {catchup.summary.open_questions.length > 0 && (
                  <div className="fx-catchup-sec"><b>Open questions</b><ul>{catchup.summary.open_questions.map((q, i) => <li key={i}>{q}</li>)}</ul></div>
                )}
              </div>
            )}
            {catchup && !catchup.summary && (
              <div className="fx-catchup-card fx-catchup-empty">
                <span>{catchup.note || 'Nothing to catch up on.'}</span>
                <button type="button" className="fx-catchup-x" onClick={() => setCatchup(null)} title="Dismiss">×</button>
              </div>
            )}
          </div>
        )}
        {messages.length === 0 && <div className="fx-empty-big">No messages yet — say hello 👋</div>}
        {messages.map((m, i) => {
          const mine = m.sender_id === meId;
          const big = jumbo && m.body && isJumbomoji(m.body);
          const prev = messages[i - 1];
          // Date separator when the calendar day changes from the previous message.
          const curDayKey = dayKey(m.created_at);
          const showDate = !!curDayKey && curDayKey !== dayKey(prev?.created_at);
          const cont =
            !!prev &&
            prev.sender_id === m.sender_id &&
            prev.sender_type === m.sender_type &&
            dividerBefore !== m.id &&
            !showDate &&
            !!m.created_at && !!prev.created_at &&
            new Date(m.created_at).getTime() - new Date(prev.created_at).getTime() < 5 * 60000;
          return (
            <React.Fragment key={m.id}>
              {showDate && m.created_at && (
                <div className="fx-date-divider"><span>{dayLabel(m.created_at)}</span></div>
              )}
              {dividerBefore === m.id && (
                <div className="fx-new-divider" ref={dividerRef}>
                  <span>New</span>
                </div>
              )}
              <div
                id={`msg-${m.id}`}
                className={`fx-msg${alwaysTs ? ' fx-msg--ts' : ''}${cont ? ' fx-msg--cont' : ''}${m.pinned ? ' fx-msg--pinned' : ''}${menuFor?.id === m.id || pickerFor === m.id || tapMsg === m.id ? ' fx-msg--active' : ''}${m.pending ? ' is-pending' : ''}`}
                role="article"
                aria-label={messageAria(p, m, senderName(m))}
                onClick={(e) => {
                  if (e.altKey && m.created_at) onMarkUnread(conversation.id, m.created_at);
                  // Touch screens have no hover: tap toggles this message's action bar
                  // (sticky :hover was leaving bars stuck open on every message).
                  if (TOUCH_ONLY) setTapMsg((cur) => (cur === m.id ? null : m.id));
                }}
              >
                <div className="fx-msgbar" role="toolbar" aria-label="Message actions">
                  {oneClick &&
                    quick.map((e) => (
                      <button type="button" key={e} className="fx-msgbar-btn fx-msgbar-emoji" title={`React ${e}`} onClick={() => toggleReaction(m, e)}>
                        {e}
                      </button>
                    ))}
                  <button type="button" className="fx-msgbar-btn" title="Add reaction" aria-label="Add reaction" onClick={(ev) => { ev.stopPropagation(); setMenuFor(null); setPickerFor(pickerFor === m.id ? null : m.id); }}>
                    <Icon name="smiley" size={16} />
                  </button>
                  {m.sender_type === 'user' && m.sender_id === meId && (
                    <button type="button" className="fx-msgbar-btn" title="Edit message" aria-label="Edit message" onClick={(ev) => { ev.stopPropagation(); editThis(m); }}>
                      <Icon name="compose" size={16} />
                    </button>
                  )}
                  <button type="button" className="fx-msgbar-btn" title="Forward message" aria-label="Forward message" onClick={(ev) => { ev.stopPropagation(); setPickerFor(null); setFwdMsg(m); }}>
                    <Icon name="forward" size={16} />
                  </button>
                  <button type="button" className="fx-msgbar-btn" title="More actions" aria-label="More actions" onClick={(ev) => openMenu(m, ev)}>
                    <Icon name="more" size={16} />
                  </button>
                  {pickerFor === m.id && (
                    <div className="fx-react-picker fx-react-picker--bar" onClick={(ev) => ev.stopPropagation()}>
                      <EmojiPicker onPick={(e) => { toggleReaction(m, applySkin(e, getPref(p, 'appearance.emoji_skin_tone', 0))); setPickerFor(null); }} />
                    </div>
                  )}
                </div>
                {cont ? (
                  <div className="fx-msg-gutter">{fmtTime(m.created_at, p)}</div>
                ) : (
                  <div className="fx-avatar" style={{ ...senderAvatar(m), ...(m.sender_type === 'agent' ? { backgroundColor: 'transparent' } : {}), cursor: m.sender_type === 'user' && onOpenProfile ? 'pointer' : undefined }}
                    onClick={m.sender_type === 'user' && onOpenProfile ? (e) => { e.stopPropagation(); onOpenProfile(m.sender_id); } : undefined}
                    title={m.sender_type === 'user' && onOpenProfile ? 'View profile' : undefined}>
                    {m.sender_type === 'agent' ? <AgentFace name={senderName(m)} avatarUrl={m.sender_avatar} size={30} /> : senderAvatar(m).backgroundImage ? '' : senderName(m)[0]?.toUpperCase()}
                  </div>
                )}
                <div className="fx-msg-body">
                  {m.pinned && !cont && (
                    <div className="fx-pinned-tag"><Icon name="pin" size={12} /> Pinned</div>
                  )}
                  {m.is_announcement && (
                    <div className="fx-ann-tag">
                      📣 Announcement
                      <button type="button" className="fx-ann-seen" onClick={() => setReceiptsFor(m.id)}>Seen by…</button>
                    </div>
                  )}
                  {m.flagged_dlp && (
                    <div className="fx-dlp-tag" title="Flagged by data-loss prevention — may contain sensitive data">⚠︎ Sensitive data</div>
                  )}
                  {!cont && (
                    <div className="fx-msg-head">
                      <span className="fx-msg-name">{senderName(m)}</span>
                      <span className="fx-msg-time">{fmtTime(m.created_at, p)}</span>
                      {m.edited_at && <span className="fx-edited">(edited)</span>}
                    </div>
                  )}
                  {m.body && (
                    <div className={`fx-msg-text${big ? ' fx-jumbo' : ''}`}>
                      {big ? m.body : renderMd(m.body, mdOpts)}
                    </div>
                  )}
                  {m.sender_type === 'agent' && (() => {
                    const appr = parseApproval(m.body);
                    if (!appr) return null;
                    return (
                      <ApprovalActions
                        cmds={appr}
                        pending={i === messages.length - 1}
                        onPick={(cmd) => api.sendMessage(conversation.id, cmd).then(() => {})}
                      />
                    );
                  })()}
                  {(m.attachments || []).map((a, ai) => (
                    a.type === 'poll' && a.poll_id
                      ? <PollCard key={a.poll_id} pollId={a.poll_id} live={pollLive[a.poll_id]} members={members} />
                      : a.type === 'prompt' && a.prompt_id
                      ? <PromptCard key={a.prompt_id} promptId={a.prompt_id} live={promptLive[a.prompt_id]} />
                      : a.type === 'prompt_answer'
                      ? null
                      : <Attach key={a.id || `att-${ai}`} att={a} inline={inlineMedia} />
                  ))}
                  {(m.reactions || []).length > 0 && (
                    <div className="fx-reactions">
                      {(m.reactions || []).map((r) => (
                        <button
                          type="button"
                          key={r.emoji}
                          className={`fx-reaction${r.actors.includes(`user:${meId}`) ? ' is-mine' : ''}`}
                          onClick={() => toggleReaction(m, r.emoji)}
                        >
                          {r.emoji} <span className="fx-reaction-count">{r.count}</span>
                        </button>
                      ))}
                      <button type="button" className="fx-react-add fx-react-more" title="Add reaction" aria-label="Add reaction" onClick={(ev) => { ev.stopPropagation(); setPickerFor(pickerFor === m.id ? null : m.id); }}>＋</button>
                    </div>
                  )}
                  {(m.reply_count || 0) > 0 && (
                    <button type="button" onClick={() => setOpenThreadRoot(m.id)}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 4, padding: '3px 9px',
                        border: '1px solid var(--fx-hair)', borderRadius: 14, background: 'transparent',
                        color: 'var(--fx-accent)', font: 'inherit', fontSize: 12.5, cursor: 'pointer', fontWeight: 600 }}>
                      💬 {m.reply_count} {m.reply_count === 1 ? 'reply' : 'replies'}
                      {m.last_reply_at && <span style={{ color: 'var(--fx-faint)', fontWeight: 400 }}> · last {fmtTime(m.last_reply_at, p)}</span>}
                    </button>
                  )}
                  {mine && m.id === myLastMsgId && seenByEveryone(m) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 3, color: 'var(--fx-faint)', fontSize: 11 }} title="Everyone in this conversation has read this">
                      <Icon name="eye" size={12} /> Seen by everyone
                    </div>
                  )}
                </div>
              </div>
            </React.Fragment>
          );
        })}
        {/* Agent "working…" status lives at the bottom of the conversation, under the
            last message — not down in the composer footer (Matt's feedback). */}
        {agentStatuses.map((s) => (
          <div className="fx-agent-status" key={s.agentId}>
            <span className="fx-agent-status-emoji">{s.emoji}</span> {s.name} — {s.text || 'working…'}
          </div>
        ))}
      </div>

      {typingNames.length > 0 && (
        <div className="fx-typing">{typingNames.join(', ')} {typingNames.length === 1 ? 'is' : 'are'} typing…</div>
      )}

      <input ref={fileRef} type="file" multiple hidden style={{ display: 'none' }} onChange={onFiles} />
      {kbHint && (
        <div className="fx-kbhint">
          <div className="fx-kbhint-head">
            <span className="fx-kbhint-lbl">📚 Possibly already answered:</span>
            <button type="button" className="fx-kbhint-title" onClick={() => setKbHintOpen((v) => !v)}>
              {kbHint.title}
            </button>
            <button type="button" className="fx-route-x" title="Dismiss"
              onClick={() => { kbDismissed.current.add(kbHint.id); setKbHint(null); }}>×</button>
          </div>
          {kbHintOpen && <div className="fx-kbhint-body">{kbHint.body}</div>}
        </div>
      )}
      {route && (route.channels.length > 0 || route.people.length > 0) && (
        <div className="fx-route">
          <span className="fx-route-lbl"><Icon name="query" size={12} /> Maybe ask in</span>
          {route.channels.map((c) => (
            <button key={c.id} type="button" className="fx-route-chip"
              title={`${c.matches} similar discussion${c.matches === 1 ? '' : 's'}`}
              onClick={() => { saveDraft(c.id, draft); onOpenConversation(c.id); setRoute(null); }}>#{c.name}</button>
          ))}
          {route.people.map((pp) => (
            <button key={pp.id} type="button" className="fx-route-chip is-person"
              title={`${pp.matches} relevant message${pp.matches === 1 ? '' : 's'}`}
              onClick={() => { onDm('user', pp.id); setRoute(null); }}>@{pp.name}</button>
          ))}
          <button type="button" className="fx-route-x" onClick={() => setRoute(null)} title="Dismiss">×</button>
        </div>
      )}
      {!editing && draft.trim().length >= 15 && user?.ai_features_enabled && (
        <div className="fx-tone">
          {!tone ? (
            <button type="button" className="fx-tone-btn" onClick={runToneCheck} disabled={toneBusy}>
              <Icon name="smiley" size={13} /> {toneBusy ? 'Checking tone…' : 'Tone check'}
            </button>
          ) : (
            <div className={`fx-tone-res is-${tone.verdict}`}>
              <span className="fx-tone-verdict">
                {tone.verdict === 'clear' ? '✓ Reads well' : tone.verdict === 'caution' ? '⚠ Could be misread' : '⚠ May sound harsh'}
              </span>
              {tone.issues.length > 0 && <span className="fx-tone-issues">{tone.issues.join(' · ')}</span>}
              {tone.rewrite && (
                <button type="button" className="fx-tone-use" onClick={() => { setDraft(tone.rewrite as string); saveDraft(conversation.id, tone.rewrite as string); setTone(null); }}>Use suggestion</button>
              )}
              <button type="button" className="fx-tone-x" onClick={() => setTone(null)} title="Dismiss">×</button>
            </div>
          )}
        </div>
      )}
      <SuggestionCards
        suggestions={suggestions}
        channelId={conversation.id}
        onAccept={(s) => {
          onSuggestionResolved(s.id);
          if (s.kind === 'action_item') toast('Added to Tasks & Notes');
          else if (s.kind === 'response') toast('Draft sent');
        }}
        onDismiss={(s) => onSuggestionResolved(s.id)}
        onInsertDraft={(text) => setDraft(text)}
      />
      <Composer
        onMentionOpen={onReloadMembers}
        value={draft}
        placeholder={`Message ${title}`}
        spellcheck={getPref(p, 'language.spellcheck', true)}
        enterSends={getPref(p, 'advanced.enter_sends', true)}
        codeFenceEnter={getPref(p, 'advanced.code_block_enter', true)}
        uploading={uploading}
        recording={recording}
        clipRecording={clipRecording}
        agentWorking={agentStatuses.length > 0}
        hasAttachments={pending.length > 0}
        attachments={pending.length > 0 && (
          <div className="fx-pending">
            {pending.map((a) => (
              <span className={`fx-chip${a.kind === 'audio' || a.kind === 'video' ? ' is-media' : ''}`} key={a.id}>
                {a.kind === 'audio' ? (
                  <>
                    <span className="fx-chip-label">🎤 Voice note{a.duration_ms ? ` · ${fmtDur(a.duration_ms)}` : ''}</span>
                    <audio className="fx-chip-audio" controls src={a.url} preload="metadata" />
                  </>
                ) : a.kind === 'video' ? (
                  <span className="fx-chip-label">🎬 Screen clip{a.duration_ms ? ` · ${fmtDur(a.duration_ms)}` : ''}</span>
                ) : (
                  <span className="fx-chip-label">📎 {a.name}</span>
                )}
                <button type="button" title="Remove" onClick={() => setPending((cur) => cur.filter((x) => x.id !== a.id))}>×</button>
              </span>
            ))}
          </div>
        )}
        members={members}
        agents={agents}
        callsEnabled={callsEnabled}
        editing={!!editing}
        upArrowMode={getPref(p, 'accessibility.up_arrow', 'edit_last')}
        onChange={(v) => {
          setDraft(v);
          if (!editing) saveDraft(conversation.id, v);
          notifyTyping();
        }}
        onSend={send}
        onPickFiles={pickFiles}
        onAddFiles={(files) => uploadFiles(files, false)}
        onVideo={onVideo}
        onToggleRecord={toggleRecord}
        onToggleClip={toggleClip}
        onCommand={onCommand}
        onSchedule={schedule}
        onViewScheduled={onViewScheduled}
        onEditLast={startEditLast}
        onFocusList={focusList}
        onCancelEdit={cancelEdit}
        workspaceId={workspaceId}
      />
        </>
        )
      )}

      {/* Canvas now hosts the AFFiNE / BlockSuite collaborative editor (was a separate Docs tab). */}
      {tab === 'canvas' && <ChannelDocsTab conversationId={conversation.id} workspaceId={workspaceId} channels={channels} onToast={toast} />}
      {tab === 'files' && (
        <ChannelFilesTab workspaceId={workspaceId} channelId={conversation.id} inlineMedia={inlineMedia} />
      )}

      {/* Overlays are portaled to <body> so their position:fixed resolves against the
          viewport, not the .fx-chat spatial-acrylic pane (a backdrop-filter/transform
          ancestor would otherwise offset fixed children by the pane's origin). */}
      {createPortal(
        <>
      {menuFor && (() => {
        const mm = messages.find((x) => x.id === menuFor.id);
        if (!mm) return null;
        const own = mm.sender_type === 'user' && mm.sender_id === meId;
        const MENU_W = 224;
        // Clamp against the menu's real height (own messages carry more items)
        // so the last rows never run past the viewport bottom.
        const MENU_H = own ? 460 : 340;
        const style: React.CSSProperties = {
          position: 'fixed',
          top: Math.max(12, Math.min(menuFor.rect.bottom + 6, window.innerHeight - MENU_H - 12)),
          left: Math.min(Math.max(12, menuFor.rect.right - MENU_W - 8), window.innerWidth - MENU_W - 12),
          width: MENU_W,
        };
        return (
          <>
            <div className="fx-menu-backdrop" onClick={() => setMenuFor(null)} />
            <div className="fx-msgmenu" style={style} role="menu">
              {own && (
                <button type="button" className="fx-msgmenu-item" role="menuitem" onClick={() => editThis(mm)}>
                  <Icon name="compose" size={15} /> <span>Edit message</span>
                </button>
              )}
              {mm.created_at && (
                <button type="button" className="fx-msgmenu-item" role="menuitem" onClick={() => { onMarkUnread(conversation.id, mm.created_at!); setMenuFor(null); }}>
                  <Icon name="markread" size={15} /> <span>Mark unread</span> <kbd className="fx-msgmenu-kbd">U</kbd>
                </button>
              )}
              <button type="button" className="fx-msgmenu-item" role="menuitem" onClick={() => copyLink(mm)}>
                <Icon name="link" size={15} /> <span>Copy link</span> <kbd className="fx-msgmenu-kbd">L</kbd>
              </button>
              <button type="button" className="fx-msgmenu-item" role="menuitem" onClick={() => { setMenuFor(null); setOpenThreadRoot(mm.thread_root_id || mm.id); }}>
                <Icon name="dm" size={15} /> <span>Reply in thread</span>
              </button>
              <button type="button" className="fx-msgmenu-item" role="menuitem" onClick={() => { setMenuFor(null); setFwdMsg(mm); }}>
                <Icon name="forward" size={15} /> <span>Forward message…</span>
              </button>
              <button type="button" className="fx-msgmenu-item" role="menuitem" onClick={() => togglePin(mm)}>
                <Icon name="pin" size={15} /> <span>{mm.pinned ? 'Unpin from channel' : 'Pin to channel'}</span> <kbd className="fx-msgmenu-kbd">P</kbd>
              </button>
              <button type="button" className="fx-msgmenu-item" role="menuitem" onClick={async () => { setMenuFor(null); try { await api.toggleAnnouncement(mm.id, !mm.is_announcement); } catch { /* author/admin only */ } }}>
                <Icon name="bell" size={15} /> <span>{mm.is_announcement ? 'Remove announcement' : 'Mark as announcement'}</span>
              </button>
              {own && (() => {
                const names = seenByFor(mm);
                return (
                  <div
                    className={`fx-msgmenu-item fx-msgmenu-seen${seenListOpen ? ' is-open' : ''}`}
                    role="menuitem" tabIndex={0}
                    onClick={() => { if (names.length > 0) setSeenListOpen((v) => !v); }}
                    onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && names.length > 0) { e.preventDefault(); setSeenListOpen((v) => !v); } }}
                  >
                    <Icon name="eye" size={15} />
                    <span>{names.length === 0 ? 'Seen by no one yet' : `Seen by ${names.length}`}</span>
                    {names.length > 0 && seenListOpen && (
                      <div className="fx-msgmenu fx-msgmenu-sub">
                        {names.map((n) => <div key={n} className="fx-msgmenu-subitem">{n}</div>)}
                      </div>
                    )}
                  </div>
                );
              })()}
              {own && (
                <>
                  <div className="fx-msgmenu-sep" />
                  <button type="button" className="fx-msgmenu-item fx-msgmenu-danger" role="menuitem" onClick={() => removeMessage(mm)}>
                    <Icon name="trash" size={15} /> <span>Delete message…</span> <kbd className="fx-msgmenu-kbd">delete</kbd>
                  </button>
                </>
              )}
            </div>
          </>
        );
      })()}

      {fwdMsg && (
        <ForwardModal
          preview={fwdMsg.body || (fwdMsg.attachments?.length ? '📎 attachment' : '')}
          sourceLabel={title}
          channels={channels}
          dms={dms}
          onPick={forwardTo}
          onClose={() => setFwdMsg(null)}
        />
      )}

      <DeclareIncidentModal
        open={declareIncidentOpen}
        workspaceId={workspaceId}
        channelId={conversation?.id || ''}
        channelName={conversation?.name}
        onClose={() => setDeclareIncidentOpen(false)}
        onDeclared={() => { setDeclareIncidentOpen(false); setIncidentReloadKey((k) => k + 1); toast('🚨 Incident declared'); }}
      />

      {openThreadRoot && conversation && (
        <ThreadPanel
          rootId={openThreadRoot}
          channelId={conversation.id}
          meId={meId}
          members={members}
          agents={agents}
          onClose={() => setOpenThreadRoot(null)}
        />
      )}

      {pinsOpen && (
        <PinsModal
          channelId={conversation.id}
          title={title}
          nameFor={senderName}
          onJump={(id) => {
            setPinsOpen(false);
            window.setTimeout(() => document.getElementById(`msg-${id}`)?.scrollIntoView({ block: 'center' }), 60);
          }}
          onClose={() => setPinsOpen(false)}
        />
      )}

      {createPollOpen && (
        <CreatePollModal
          onCreate={async (question, options, opts) => { await api.createPoll(conversation.id, question, options, opts); }}
          onClose={() => setCreatePollOpen(false)}
        />
      )}

      {checklistOpen && (
        <ChecklistModal cid={conversation.id} channelName={title} onClose={() => setChecklistOpen(false)} />
      )}

      {receiptsFor && (
        <ReceiptsModal messageId={receiptsFor} onClose={() => setReceiptsFor(null)} />
      )}

      {peopleOpen && (
        <AddPeopleModal
          candidates={members.filter((m) => !(chMembers?.users || []).some((u) => u.id === m.id))}
          onAdd={async (ids) => {
            await api.addChannelPeople(conversation.id, ids);
            setPeopleOpen(false);
            refreshMembers();
            toast(ids.length === 1 ? 'Added to channel' : `Added ${ids.length} people`);
          }}
          onClose={() => setPeopleOpen(false)}
        />
      )}

      {!isDm && (
        <ChannelDetails
          open={detailsOpen}
          onOpenChange={setDetailsOpen}
          initialTab={detailsTab}
          conversation={conversation}
          members={chMembers}
          meId={meId}
          isMuted={isMuted}
          onToggleMute={onToggleMute}
          onCopyLink={copyConvLink}
          onAddPeople={() => { setDetailsOpen(false); setPeopleOpen(true); }}
          onAddAgent={() => { setDetailsOpen(false); onAddAgentToChannel(); }}
          onLeave={() => { setDetailsOpen(false); onLeaveChannel(conversation.id); }}
          onDelete={() => { setDetailsOpen(false); onDeleteChannel(conversation.id); }}
          canDelete={!!canManageWorkspace || (!!meId && conversation.created_by === meId)}
          workspaceId={workspaceId}
          canManage={canManageWorkspace}
        />
      )}

      {memberMenu && (() => {
        const MENU_W = 220;
        const style: React.CSSProperties = {
          position: 'fixed',
          top: Math.min(memberMenu.rect.bottom + 6, window.innerHeight - 170),
          left: Math.min(Math.max(12, memberMenu.rect.right - MENU_W), window.innerWidth - MENU_W - 12),
          width: MENU_W,
        };
        return (
          <>
            <div className="fx-menu-backdrop" onClick={() => setMemberMenu(null)} />
            <div className="fx-msgmenu" style={style} role="menu">
              <div className="fx-msgmenu-head">{memberMenu.name}</div>
              {memberMenu.kind === 'user' && onOpenProfile && (
                <button
                  type="button"
                  className="fx-msgmenu-item"
                  role="menuitem"
                  onClick={() => { const { id } = memberMenu; setMemberMenu(null); setMembersOpen(false); onOpenProfile(id); }}
                >
                  <Icon name="people" size={15} /> <span>View profile</span>
                </button>
              )}
              <button
                type="button"
                className="fx-msgmenu-item"
                role="menuitem"
                onClick={() => { const { kind, id } = memberMenu; setMemberMenu(null); setMembersOpen(false); onDm(kind, id); }}
              >
                <Icon name="dm" size={15} /> <span>Message</span>
              </button>
              {(() => {
                const vip = isVip(p, memberMenu.id);
                return (
                  <button
                    type="button"
                    className={`fx-msgmenu-item${vip ? ' is-on' : ''}`}
                    role="menuitem"
                    onClick={() => {
                      const cur = getPref<string[]>(p, 'notifications.vip_people', []) || [];
                      setPref('notifications.vip_people', vip ? cur.filter((x) => x !== memberMenu.id) : [...cur, memberMenu.id]);
                      setMemberMenu(null);
                    }}
                  >
                    <Icon name="bell" size={15} /> <span>{vip ? 'Stop always notifying' : 'Always notify me'}</span>
                  </button>
                );
              })()}
              {memberMenu.kind === 'agent' ? (
                <button type="button" className="fx-msgmenu-item fx-msgmenu-danger" role="menuitem" onClick={() => doRemoveAgent(memberMenu.id)}>
                  <Icon name="close" size={15} /> <span>Remove from channel</span>
                </button>
              ) : (
                <>
                  {/* Removal from a public channel is a no-op (no per-user membership rows),
                      so only offer it on private channels; workspace removal covers the rest. */}
                  {isPrivate && (isAdmin || canManageWorkspace) && (
                    <button type="button" className="fx-msgmenu-item" role="menuitem" onClick={() => doRemoveFromChannel(memberMenu.id)}>
                      <Icon name="close" size={15} /> <span>Remove from channel</span>
                    </button>
                  )}
                  {(isAdmin || canManageWorkspace) && (
                    <button type="button" className="fx-msgmenu-item fx-msgmenu-danger" role="menuitem" onClick={() => doRemoveFromWorkspace(memberMenu.id)}>
                      <Icon name="trash" size={15} /> <span>Remove from workspace</span>
                    </button>
                  )}
                </>
              )}
            </div>
          </>
        );
      })()}

      {flash && <div className="fx-flash" role="status">{flash}</div>}
        </>,
        document.body,
      )}
    </main>
  );
}

function ForwardModal({
  preview,
  sourceLabel,
  channels,
  dms,
  onPick,
  onClose,
}: {
  preview: string;
  sourceLabel: string;
  channels: Channel[];
  dms: Dm[];
  onPick: (id: string) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const ql = q.trim().toLowerCase();
  const chOpts = channels
    .map((c) => ({ id: c.id, label: `# ${c.name}`, kind: 'channel' }))
    .filter((o) => !ql || o.label.toLowerCase().includes(ql));
  const dmOpts = dms
    .map((d) => ({ id: d.id, label: d.dm_with?.name || d.name || 'Direct message', kind: 'dm' }))
    .filter((o) => !ql || o.label.toLowerCase().includes(ql));
  return (
    <div className="fx-modal-backdrop" onClick={onClose}>
      <div className="fx-modal fx-fwd" onClick={(e) => e.stopPropagation()}>
        <div className="fx-login-title">Forward message</div>
        <div className="fx-fwd-preview">{preview || '(no text)'}</div>
        <input
          className="fx-input"
          autoFocus
          placeholder="Forward to a channel or person…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div className="fx-fwd-list">
          {chOpts.length === 0 && dmOpts.length === 0 && <div className="fx-empty-small">No matches</div>}
          {chOpts.map((o) => (
            <button type="button" key={o.id} className="fx-fwd-row" onClick={() => onPick(o.id)}>
              <Icon name="hash" size={15} /> <span>{o.label.replace(/^# /, '')}</span>
            </button>
          ))}
          {dmOpts.map((o) => (
            <button type="button" key={o.id} className="fx-fwd-row" onClick={() => onPick(o.id)}>
              <Icon name="dm" size={15} /> <span>{o.label}</span>
            </button>
          ))}
        </div>
        <div className="fx-modal-actions">
          <button type="button" className="fx-login-back" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function PinsModal({
  channelId,
  title,
  nameFor,
  onJump,
  onClose,
}: {
  channelId: string;
  title: string;
  nameFor: (m: Message) => string;
  onJump: (id: string) => void;
  onClose: () => void;
}) {
  const [pins, setPins] = useState<Message[] | null>(null);
  const load = useCallback(() => {
    api.channelPins(channelId).then(setPins).catch(() => setPins([]));
  }, [channelId]);
  useEffect(() => { load(); }, [load]);
  const unpin = async (m: Message) => {
    try {
      await api.pinMessage(m.id);
      setPins((cur) => (cur || []).filter((x) => x.id !== m.id));
    } catch { /* ignore */ }
  };
  return (
    <div className="fx-modal-backdrop" onClick={onClose}>
      <div className="fx-modal fx-dir" onClick={(e) => e.stopPropagation()}>
        <div className="fx-login-title">Pinned in {title}</div>
        {pins === null && <div className="fx-empty-small">Loading…</div>}
        {pins !== null && pins.length === 0 && (
          <div className="fx-empty-small">Nothing pinned yet — open a message’s ⋮ menu and choose “Pin to channel”.</div>
        )}
        {(pins || []).map((m) => (
          <div className="fx-pin-row" key={m.id}>
            <button type="button" className="fx-pin-main" onClick={() => onJump(m.id)}>
              <div className="fx-pin-head">
                <span className="fx-pin-name">{nameFor(m)}</span>
                <span className="fx-pin-time">{m.created_at ? new Date(m.created_at).toLocaleDateString() : ''}</span>
              </div>
              <div className="fx-pin-body">{m.body || (m.attachments?.length ? '📎 attachment' : '')}</div>
            </button>
            <button type="button" className="fx-pin-unpin" title="Unpin" onClick={() => unpin(m)}>
              <Icon name="close" size={14} />
            </button>
          </div>
        ))}
        <div className="fx-modal-actions">
          <button type="button" className="fx-login-back" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

function fmtDur(ms?: number): string {
  if (!ms || ms < 0) return '';
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Auto-transcription readout for a voice note / video clip. Live-updates when the
 *  server's Whisper task lands (message.update swaps in the transcript). */
function Transcript({ att }: { att: Attachment }) {
  if (att.transcript) {
    return (
      <details className="fx-transcript">
        <summary>Transcript</summary>
        <p className="fx-transcript-text">{att.transcript}</p>
      </details>
    );
  }
  if (att.transcript_status === 'pending') {
    return <div className="fx-transcript-pending"><span className="fx-tspin" /> Transcribing…</div>;
  }
  if (att.transcript_status === 'quota') {
    return <div className="fx-transcript-note">Transcript unavailable — transcription credits exhausted</div>;
  }
  if (att.transcript_status === 'error') {
    return <div className="fx-transcript-note">Transcript unavailable</div>;
  }
  return null;
}

function Attach({ att, inline }: { att: Attachment; inline: boolean }) {
  const mime = att.mime || '';
  const isImage = inline && mime.startsWith('image/');
  const isAudio = inline && (att.kind === 'audio' || mime.startsWith('audio/'));
  const isVideo = inline && (att.kind === 'video' || mime.startsWith('video/'));
  if (isImage) {
    return (
      <a href={att.url} target="_blank" rel="noreferrer">
        <img className="fx-image" src={att.url} alt={att.alt || att.name} title={att.alt} />
      </a>
    );
  }
  if (isAudio) {
    return (
      <div className="fx-audio">
        <div className="fx-audio-row">
          🎤 <audio controls src={att.url} preload="metadata" />
          {att.duration_ms ? <span className="fx-dur">{fmtDur(att.duration_ms)}</span> : null}
        </div>
        <Transcript att={att} />
      </div>
    );
  }
  if (isVideo) {
    return (
      <div className="fx-video">
        <video className="fx-clip" controls src={att.url} poster={att.poster || undefined} preload="metadata" />
        <Transcript att={att} />
      </div>
    );
  }
  const ext = ((att.name || '').split('.').pop() || '').toUpperCase();
  return (
    <a href={att.url} target="_blank" rel="noreferrer" className="fx-file is-card" title={att.name}>
      <FileBadge name={att.name || ''} mime={mime} />
      <span className="fx-file-info">
        <span className="fx-file-nm2">{att.name}</span>
        <span className="fx-file-sub2">
          {ext && ext.length <= 5 && ext !== (att.name || '').toUpperCase() ? `${ext} · ` : ''}
          {att.size ? `${Math.max(1, Math.round(att.size / 1024))} KB` : 'File'}
        </span>
      </span>
      <span className="fx-file-dl" aria-hidden><Icon name="download" size={15} /></span>
    </a>
  );
}

type ScopeGrp = { group: string; scopes: { scope: string; desc: string }[] };

function AdminModal({
  wid,
  agents,
  onCreate,
  onRotate,
  onReloadAgents,
  onBack,
  onClose,
}: {
  wid: string;
  agents: Agent[];
  onCreate: () => void;
  onRotate: (a: Agent) => void;
  onReloadAgents: () => void;
  onBack?: () => void;
  onClose: () => void;
}) {
  const [catalog, setCatalog] = useState<{ bot: ScopeGrp[]; app: ScopeGrp[] } | null>(null);
  const [tab, setTab] = useState<'bots' | 'app' | 'connect'>('bots');
  const [connTok, setConnTok] = useState('');
  const [connKind, setConnKind] = useState<'hermes' | 'openclaw'>('hermes');
  const [cmdCopied, setCmdCopied] = useState(false);
  const [openAgent, setOpenAgent] = useState<string | null>(null);
  const [detailFor, setDetailFor] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, string[]>>({});
  const [saving, setSaving] = useState(false);
  const [appTok, setAppTok] = useState<{ exists: boolean; scopes: string[]; last_rotated_at: string | null } | null>(null);
  const [appScopes, setAppScopes] = useState<string[]>([]);
  const [reveal, setReveal] = useState<string | null>(null);

  useEffect(() => { api.scopesCatalog().then(setCatalog).catch(() => {}); }, []);
  useEffect(() => { api.getAppToken(wid).then((t) => { setAppTok(t); setAppScopes(t.scopes || []); }).catch(() => {}); }, [wid]);

  const openScopes = (a: Agent) => {
    setDraft((d) => ({ ...d, [a.id]: a.scopes || [] }));
    setOpenAgent((cur) => (cur === a.id ? null : a.id));
  };
  const toggle = (aid: string, s: string) => setDraft((d) => {
    const cur = d[aid] || [];
    return { ...d, [aid]: cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s] };
  });
  const save = async (a: Agent) => {
    setSaving(true);
    try { await api.setAgentScopes(wid, a.id, draft[a.id] || []); onReloadAgents(); setOpenAgent(null); }
    finally { setSaving(false); }
  };
  const toggleApp = (s: string) => setAppScopes((v) => (v.includes(s) ? v.filter((x) => x !== s) : [...v, s]));
  const rotateApp = async () => {
    const r = await api.rotateAppToken(wid, appScopes);
    setReveal(r.token);
    api.getAppToken(wid).then(setAppTok).catch(() => {});
  };

  const scopeList = (groups: ScopeGrp[], checked: (s: string) => boolean, onToggle: (s: string) => void) => (
    <div className="fx-scopes">
      {groups.map((grp) => (
        <div className="fx-scope-grp" key={grp.group}>
          <div className="fx-scope-ghead">{grp.group}</div>
          {grp.scopes.map((s) => (
            <label className="fx-scope" key={s.scope}>
              <input type="checkbox" checked={checked(s.scope)} onChange={() => onToggle(s.scope)} />
              <span className="fx-scope-name">{s.scope}</span>
              <span className="fx-scope-desc">{s.desc}</span>
            </label>
          ))}
        </div>
      ))}
    </div>
  );

  return (
    <div className="fx-modal-backdrop" onClick={onClose}>
      <div className="fx-modal fx-bots" onClick={(e) => e.stopPropagation()}>
        <div className="fx-bots-head">
          <div className="fx-login-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {(detailFor || onBack) && (
              <button
                type="button"
                className="fx-btn"
                style={{ padding: '3px 10px', fontSize: 16, lineHeight: 1.2 }}
                aria-label={detailFor ? 'Back to bots' : 'Back to Admin'}
                title={detailFor ? 'Back to bots' : 'Back to Admin'}
                onClick={() => (detailFor ? setDetailFor(null) : onBack?.())}
              >
                ←
              </button>
            )}
            {detailFor ? (agents.find((x) => x.id === detailFor)?.name || 'Bot') : 'Bots & apps'}
          </div>
          {!detailFor && (
            <div className="fx-bots-tabs">
              <button type="button" className={`fx-bots-tab${tab === 'bots' ? ' is-active' : ''}`} onClick={() => setTab('bots')}>Bots</button>
              <button type="button" className={`fx-bots-tab${tab === 'connect' ? ' is-active' : ''}`} onClick={() => setTab('connect')}>Connect an agent</button>
              <button type="button" className={`fx-bots-tab${tab === 'app' ? ' is-active' : ''}`} onClick={() => setTab('app')}>App-level token</button>
            </div>
          )}
        </div>

        <div className="fx-bots-body">
          {tab === 'bots' && !detailFor && (
            <>
              <div className="fx-bots-intro">
                A bot token authenticates any external app or tool as this bot. Select a bot to
                manage its avatar, scopes and token.
              </div>
              {agents.length === 0 && <div className="fx-empty">No bots yet. Add one to connect an agent, integration, or tool.</div>}
              {agents.map((a) => {
                const count = (a.scopes || []).length;
                return (
                  <button
                    type="button"
                    className="fx-bot fx-bot-row"
                    key={a.id}
                    style={{ width: '100%', textAlign: 'left', cursor: 'pointer' }}
                    onClick={() => { openScopes(a); setOpenAgent(null); setDetailFor(a.id); }}
                  >
                    <span className="fx-agent-av" style={{ backgroundColor: 'transparent' }}><AgentFace name={a.name} avatarUrl={a.avatar_url} size={28} /></span>
                    <span className="fx-dir-name">{a.name}</span>
                    <span className={`fx-bot-scopecount${count === 0 ? ' is-open' : ''}`}>
                      {count === 0 ? 'full access' : `${count} scope${count === 1 ? '' : 's'}`}
                    </span>
                    <span aria-hidden style={{ color: 'var(--fx-muted)', fontSize: 16 }}>›</span>
                  </button>
                );
              })}
            </>
          )}

          {tab === 'bots' && detailFor && (() => {
            const a = agents.find((x) => x.id === detailFor);
            if (!a) return null;
            const count = (a.scopes || []).length;
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <AgentFace name={a.name} avatarUrl={a.avatar_url} size={44} />
                  <div>
                    <div style={{ fontWeight: 700 }}>{a.name}</div>
                    <div style={{ fontSize: 12.5, color: 'var(--fx-muted)' }}>
                      {count === 0 ? 'Full access — no scopes selected yet' : `${count} scope${count === 1 ? '' : 's'} granted`}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="fx-dlg-label" style={{ marginBottom: 8 }}>Avatar</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(64px, 1fr))', gap: 8 }}>
                    {ROBOT_VARIANTS.map((v) => (
                      <button
                        type="button"
                        key={v.key}
                        className="fx-btn"
                        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '8px 4px', ...(robotVariantOf(a.avatar_url) === v.key || (!robotVariantOf(a.avatar_url) && v.key === 'classic') ? { borderColor: 'var(--fx-accent)' } : {}) }}
                        title={v.label}
                        onClick={async () => {
                          try { await api.setAgentAvatar(a.id, `robot:${v.key}`); onReloadAgents(); }
                          catch (e: any) { window.alert(e?.message || 'Could not set the avatar.'); }
                        }}
                      >
                        <AgentAvatar name={a.name} variant={v.key} size={34} />
                        <span style={{ fontSize: 10.5 }}>{v.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="fx-dlg-label" style={{ marginBottom: 4 }}>Access</div>
                  <div className="fx-bots-intro" style={{ marginTop: 0 }}>
                    The token is limited to the scopes you select — pick only what it needs.
                    No scopes selected means full access, so scope it down.
                  </div>
                  {catalog && (
                    <>
                      {scopeList(catalog.bot, (s) => (draft[a.id] || []).includes(s), (s) => toggle(a.id, s))}
                      <div className="fx-modal-actions" style={{ padding: '4px 0 0' }}>
                        <span className="fx-scope-selcount">{(draft[a.id] || []).length} selected</span>
                        <button type="button" className="fx-sendbtn" disabled={saving} onClick={() => save(a)}>{saving ? 'Saving…' : 'Save scopes'}</button>
                      </div>
                    </>
                  )}
                </div>

                <div>
                  <div className="fx-dlg-label" style={{ marginBottom: 8 }}>Token</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button type="button" className="fx-btn" onClick={() => onRotate(a)}>Rotate token</button>
                    <button
                      type="button"
                      className="fx-btn"
                      style={{ color: '#e56a6a' }}
                      title="Remove this agent from the workspace"
                      onClick={async () => {
                        if (!window.confirm(`Remove “${a.name}” from this workspace? Its token stops working and it leaves every channel.`)) return;
                        try { await api.deleteAgent(wid, a.id); onReloadAgents(); setDetailFor(null); } catch (e: any) { window.alert(e?.message || 'Could not remove the agent.'); }
                      }}
                    >
                      Remove from workspace
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}

          {tab === 'connect' && (() => {
            const tok = connTok.trim() || 'agt_YOUR_BOT_TOKEN';
            const cmd = `curl -fsSL ${API_BASE}/plugin/${connKind}/install.sh | FXM_BOT_TOKEN=${tok} bash`;
            const copyCmd = async () => {
              try { await navigator.clipboard.writeText(cmd); setCmdCopied(true); setTimeout(() => setCmdCopied(false), 1600); } catch { /* manual select */ }
            };
            const oc = connKind === 'openclaw';
            return (
              <>
                <div className="fx-cc-seg" role="tablist" style={{ margin: '2px 0 12px' }}>
                  <button type="button" className={`fx-cc-seg-btn${!oc ? ' is-active' : ''}`} onClick={() => setConnKind('hermes')}>Hermes</button>
                  <button type="button" className={`fx-cc-seg-btn${oc ? ' is-active' : ''}`} onClick={() => setConnKind('openclaw')}>OpenClaw</button>
                </div>
                <div className="fx-bots-intro">
                  Connect <strong>any {oc ? 'OpenClaw' : 'Hermes'} agent</strong> — not just getfxm FMAs — to this
                  workspace. The agent runs the native FxM {oc ? 'channel' : 'platform'} plugin: its own gateway opens
                  the FxM realtime socket and replies over the Bot API. No bridge process.
                </div>
                <ol className="fx-connect-steps">
                  <li>On the <button type="button" className="fx-linkbtn" onClick={() => setTab('bots')}>Bots</button> tab, add a bot and <em>Rotate token</em> (shown once). Grant it <code>chat:write</code>.</li>
                  <li>Paste that token here:</li>
                </ol>
                <input
                  className="fx-text-input"
                  placeholder="agt_…"
                  value={connTok}
                  onChange={(e) => setConnTok(e.target.value)}
                  spellCheck={false}
                  autoCapitalize="off"
                  autoCorrect="off"
                />
                <div className="fx-connect-cmdlabel">Then run this on the machine that hosts your agent:</div>
                <code className="fx-token fx-connect-cmd">{cmd}</code>
                <button type="button" className="fx-sendbtn" onClick={copyCmd}>{cmdCopied ? 'Copied ✓' : 'Copy command'}</button>
                {oc ? (
                  <div className="fx-connect-manual">
                    Installing by hand? Drop{' '}
                    <a className="fx-linkbtn" href={`${API_BASE}/plugin/openclaw/files/package.json`} target="_blank" rel="noreferrer">package.json</a>,{' '}
                    <a className="fx-linkbtn" href={`${API_BASE}/plugin/openclaw/files/openclaw.plugin.json`} target="_blank" rel="noreferrer">openclaw.plugin.json</a>,{' '}
                    <a className="fx-linkbtn" href={`${API_BASE}/plugin/openclaw/files/index.mjs`} target="_blank" rel="noreferrer">index.mjs</a>,{' '}
                    <a className="fx-linkbtn" href={`${API_BASE}/plugin/openclaw/files/runtime.mjs`} target="_blank" rel="noreferrer">runtime.mjs</a>{' '}
                    into <code>~/.openclaw/plugins/fxm/</code>, run <code>openclaw plugins install --link</code> on that dir, set{' '}
                    <code>channels.fxm.botToken</code> in <code>openclaw.json</code>, and restart the gateway.
                  </div>
                ) : (
                  <div className="fx-connect-manual">
                    Installing by hand? Drop{' '}
                    <a className="fx-linkbtn" href={`${API_BASE}/plugin/hermes/files/adapter.py`} target="_blank" rel="noreferrer">adapter.py</a>,{' '}
                    <a className="fx-linkbtn" href={`${API_BASE}/plugin/hermes/files/plugin.yaml`} target="_blank" rel="noreferrer">plugin.yaml</a>,{' '}
                    <a className="fx-linkbtn" href={`${API_BASE}/plugin/hermes/files/__init__.py`} target="_blank" rel="noreferrer">__init__.py</a>{' '}
                    into <code>~/.hermes/plugins/fxm/</code>, set <code>FXM_BOT_TOKEN</code>, enable{' '}
                    <code>plugins.enabled: ["fxm-platform"]</code> in <code>config.yaml</code>, and restart Hermes.
                  </div>
                )}
              </>
            );
          })()}

          {tab === 'app' && catalog && (
            <>
              <div className="fx-bots-intro">
                An app-level token lets any external app or tool (an agent, integration, bot, or
                workflow) open a socket connection and receive workspace events in real time.
                {appTok?.exists
                  ? ` Active — last rotated ${appTok.last_rotated_at ? new Date(appTok.last_rotated_at).toLocaleString() : ''}.`
                  : ' No app token yet.'}
              </div>
              {scopeList(catalog.app, (s) => appScopes.includes(s), toggleApp)}
            </>
          )}
        </div>

        <div className="fx-bots-foot">
          {tab === 'bots' && (
            <button type="button" className="fx-sendbtn" onClick={onCreate}>New bot</button>
          )}
          {tab === 'app' && (
            <button type="button" className="fx-sendbtn" onClick={rotateApp}>{appTok?.exists ? 'Rotate app token' : 'Generate app token'}</button>
          )}
          <button type="button" className="fx-login-back" onClick={onClose}>Done</button>
        </div>
      </div>
      {reveal && <AgentTokenModal data={{ name: 'App-level token', token: reveal }} onClose={() => setReveal(null)} />}
    </div>
  );
}

function AgentTokenModal({ data, onClose }: { data: { name: string; token: string }; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const [cmdCopied, setCmdCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(data.token); setCopied(true); } catch { /* manual select */ }
  };
  const isBot = data.token.startsWith('agt_');
  const cmd = `curl -fsSL ${API_BASE}/plugin/hermes/install.sh | FXM_BOT_TOKEN=${data.token} bash`;
  const copyCmd = async () => {
    try { await navigator.clipboard.writeText(cmd); setCmdCopied(true); setTimeout(() => setCmdCopied(false), 1600); } catch { /* manual select */ }
  };
  return (
    <div className="fx-modal-backdrop" onClick={onClose}>
      <div className="fx-modal" onClick={(e) => e.stopPropagation()}>
        <div className="fx-login-title">{data.name} — bot token</div>
        <div className="fx-login-note" style={{ textAlign: 'left' }}>
          Copy this now — it’s shown only once. Give it to the agent/FMA as its
          <code> Authorization: Bearer</code> token for the bot API.
        </div>
        <code className="fx-token">{data.token}</code>
        <button type="button" className="fx-sendbtn" onClick={copy}>{copied ? 'Copied ✓' : 'Copy token'}</button>
        {isBot && (
          <>
            <div className="fx-login-note" style={{ textAlign: 'left', marginTop: 14 }}>
              Running a Hermes agent? Connect it in one line:
            </div>
            <code className="fx-token fx-connect-cmd">{cmd}</code>
            <button type="button" className="fx-sendbtn" onClick={copyCmd}>{cmdCopied ? 'Copied ✓' : 'Copy install command'}</button>
          </>
        )}
        <div className="fx-modal-actions">
          <button type="button" className="fx-login-back" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

function CreateChannelModal({ members, meId, onCreate, onClose }: {
  members: PublicUser[];
  meId?: string;
  onCreate: (name: string, kind: 'public' | 'private', memberIds: string[]) => Promise<void>;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [kind, setKind] = useState<'public' | 'private'>('public');
  const [picked, setPicked] = useState<string[]>([]);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  // Ref guard: state alone races — two quick Enter presses (key auto-repeat) both
  // read busy=false before the re-render lands, creating the channel twice.
  const inFlight = useRef(false);
  const others = members.filter((m) => m.id !== meId);
  const byId = (id: string) => others.find((m) => m.id === id);
  const ql = q.trim().toLowerCase();
  const results = ql
    ? others.filter((m) => !picked.includes(m.id) && `${m.name || ''} ${m.email || ''}`.toLowerCase().includes(ql)).slice(0, 8)
    : [];
  const add = (id: string) => { setPicked((p) => [...p, id]); setQ(''); };
  const remove = (id: string) => setPicked((p) => p.filter((x) => x !== id));
  const submit = async () => {
    if (!name.trim() || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    try { await onCreate(name.trim(), kind, picked); }
    finally { inFlight.current = false; setBusy(false); }
  };
  return (
    <div className="fx-modal-backdrop" onClick={onClose}>
      <div className="fx-modal" onClick={(e) => e.stopPropagation()}>
        <div className="fx-login-title">Create a channel</div>
        <input
          className="fx-text-input"
          placeholder="e.g. marketing"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          autoFocus
        />
        <div className="fx-cc-seg" role="tablist">
          {(['public', 'private'] as const).map((k) => (
            <button key={k} type="button" role="tab" aria-selected={kind === k} className={`fx-cc-seg-btn${kind === k ? ' is-on' : ''}`} onClick={() => setKind(k)}>
              {k === 'public' ? '# Public' : '🔒 Private'}
            </button>
          ))}
        </div>
        <div className="fx-login-note" style={{ textAlign: 'left', marginTop: 8, fontSize: 12 }}>
          {kind === 'public'
            ? 'Anyone in the workspace can find and view this channel. Add people to drop them straight in.'
            : 'Only the people you add can see this channel.'}
        </div>
        <div className="fx-cc-picker">
          <div className="fx-invite-ch-h">Add people {picked.length > 0 && <span>({picked.length})</span>}</div>
          {picked.length > 0 && (
            <div className="fx-cc-chips">
              {picked.map((id) => {
                const m = byId(id);
                return m ? (
                  <span className="fx-cc-chip" key={id}>
                    {m.name || m.email}
                    <button type="button" aria-label="Remove" onClick={() => remove(id)}>×</button>
                  </span>
                ) : null;
              })}
            </div>
          )}
          <input className="fx-text-input" placeholder="Search people by name…" value={q} onChange={(e) => setQ(e.target.value)} spellCheck={false} />
          {results.length > 0 && (
            <div className="fx-cc-results">
              {results.map((m) => (
                <button type="button" className="fx-cc-result" key={m.id} onClick={() => add(m.id)}>
                  <span className="fx-cc-av" style={avatarStyle(m)}>{m.avatar_url ? '' : (m.name || m.email || '?')[0]?.toUpperCase()}</span>
                  <span className="fx-cc-result-nm">{m.name || m.email}</span>
                </button>
              ))}
            </div>
          )}
          {ql && results.length === 0 && <div className="fx-empty" style={{ padding: '6px 2px' }}>No matches.</div>}
        </div>
        <div className="fx-modal-actions">
          <button type="button" className="fx-sendbtn" disabled={!name.trim() || busy} onClick={submit}>{busy ? 'Creating…' : 'Create channel'}</button>
          <button type="button" className="fx-login-back" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function CreateFmaModal({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState('');
  const [tier, setTier] = useState<'starter' | 'pro' | 'ultra'>('pro');
  const [tos, setTos] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const TIERS: { key: 'starter' | 'pro' | 'ultra'; label: string; blurb: string }[] = [
    { key: 'starter', label: 'Starter', blurb: 'For trying things out' },
    { key: 'pro', label: 'Pro', blurb: 'More capacity + higher limits' },
    { key: 'ultra', label: 'Ultra', blurb: 'Highest limits' },
  ];
  const go = async () => {
    setErr('');
    if (!tos) { setErr('Please accept the Terms of Service to continue.'); return; }
    setBusy(true);
    try {
      const { checkout_url } = await api.createFma({ bot_name: name.trim(), tier, tos_accepted: true });
      window.open(checkout_url, '_blank', 'noopener');
      onClose();
    } catch (e: any) {
      setErr(e?.message || 'Could not start checkout. Please try again.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fx-modal-backdrop" onClick={onClose}>
      <div className="fx-modal fx-modal--invite" onClick={(e) => e.stopPropagation()}>
        <div className="fx-login-title">Create a new FMA agent</div>
        <div className="fx-invite-sec">
          <div className="fx-login-note" style={{ textAlign: 'left', marginTop: 0 }}>
            Spin up your own Force Multiplier agent. You'll finish on a secure checkout page; once
            it's set up, the agent appears in your workspace automatically.
          </div>
          <div className="fx-invite-h" style={{ marginTop: 12 }}>Agent name</div>
          <input className="fx-text-input" placeholder="e.g. Support Bot" value={name}
            onChange={(e) => setName(e.target.value)} maxLength={80} />
          <div className="fx-invite-h" style={{ marginTop: 14 }}>Plan</div>
          <div className="fx-fma-tiers">
            {TIERS.map((t) => (
              <button type="button" key={t.key} className={`fx-fma-tier${tier === t.key ? ' is-on' : ''}`}
                onClick={() => setTier(t.key)}>
                <span className="fx-fma-tier-name">{t.label}</span>
                <span className="fx-fma-tier-blurb">{t.blurb}</span>
              </button>
            ))}
          </div>
          <label className="fx-fma-tos">
            <input type="checkbox" checked={tos} onChange={(e) => setTos(e.target.checked)} />
            <span>I agree to the <a href="https://selfserve.getfxm.ai/terms" target="_blank" rel="noreferrer">Terms of Service</a>.</span>
          </label>
          {err && <div className="fx-login-err">{err}</div>}
          <div className="fx-modal-actions">
            <button type="button" className="fx-sendbtn fx-login-btn" onClick={go} disabled={busy || !tos}>
              {busy ? 'Starting…' : 'Continue to checkout'}
            </button>
            <button type="button" className="fx-login-back" onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// People — a full dock view with three tabs (Directory / Invite people / Join requests),
// reusing the existing modals in embedded (content-only) mode. Directory is searchable.
function PeopleView({
  workspaceId, workspaceName, agents, channels, canManage, pendingCount, onDm, onOpenProfile,
}: {
  workspaceId: string;
  workspaceName: string;
  agents: Agent[];
  channels: Channel[];
  canManage: boolean;
  pendingCount: number;
  onDm: (type: 'user' | 'agent', id: string) => void;
  onOpenProfile?: (userId: string) => void;
}) {
  const [tab, setTab] = useState<'directory' | 'invite' | 'requests'>('directory');
  const tabs = [
    { key: 'directory' as const, label: 'Directory', show: true },
    { key: 'invite' as const, label: 'Invite people', show: canManage },
    { key: 'requests' as const, label: `Join requests${pendingCount ? ` (${pendingCount})` : ''}`, show: canManage },
  ].filter((t) => t.show);
  return (
    <div className="fx-chat" style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, padding: 0 }}>
      <header style={{ padding: '16px 20px 0' }}>
        <h1 style={{ margin: '0 0 10px', fontSize: 20, fontWeight: 750, color: 'var(--fx-text)' }}>People</h1>
        <nav className="fx-chan-tabs">
          {tabs.map((t) => (
            <button key={t.key} type="button" className={`fx-chan-tab${tab === t.key ? ' is-active' : ''}`} onClick={() => setTab(t.key)}>
              {t.label}
            </button>
          ))}
        </nav>
      </header>
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px 24px', minHeight: 0 }}>
        {tab === 'directory' && (
          <DirectoryModal embedded workspaceId={workspaceId} agents={agents} onDm={onDm} onOpenProfile={onOpenProfile} onClose={() => {}} />
        )}
        {tab === 'invite' && canManage && (
          <InviteModal embedded wid={workspaceId} workspaceName={workspaceName} channels={channels} onClose={() => {}} />
        )}
        {tab === 'requests' && canManage && (
          <ApprovalsModal embedded workspaceId={workspaceId} onClose={() => {}} />
        )}
      </div>
    </div>
  );
}

function InviteModal({ wid, workspaceName, channels, onClose, embedded }: { wid: string; workspaceName: string; channels: Channel[]; onClose: () => void; embedded?: boolean }) {
  const [url, setUrl] = useState<string | null>(null);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [emails, setEmails] = useState('');
  const [sending, setSending] = useState(false);
  const [sentNote, setSentNote] = useState('');
  const [addCh, setAddCh] = useState<string[]>([]);
  const [role, setRole] = useState<'member' | 'guest'>('member');
  const [tab, setTab] = useState<'invite' | 'people'>('invite');
  const [revoking, setRevoking] = useState('');
  const [invites, setInvites] = useState<{ email: string; role: string; accepted: boolean; created_at: string | null }[]>([]);
  const toggleCh = (id: string) => setAddCh((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));

  useEffect(() => {
    let alive = true;
    api.getInvite(wid).then((r) => { if (alive) setUrl(r.url); }).catch((e: any) => { if (alive) setErr(e?.message || 'Could not load invite link'); });
    return () => { alive = false; };
  }, [wid]);
  useEffect(() => { api.invitations(wid).then(setInvites).catch(() => {}); }, [wid, sentNote]);

  const copy = async () => {
    if (!url) return;
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1600); } catch { /* manual select */ }
  };
  const rotate = async () => {
    setRotating(true);
    try { const r = await api.rotateInvite(wid); setUrl(r.url); setCopied(false); }
    catch (e: any) { setErr(e?.message || 'Could not reset link'); }
    finally { setRotating(false); }
  };
  const sendEmails = async () => {
    const list = emails.split(/[\s,;]+/).map((s) => s.trim()).filter((s) => s.includes('@'));
    if (!list.length) { setErr('Enter one or more email addresses'); return; }
    if (role === 'guest' && !addCh.length) { setErr('Pick at least one channel — guests only see the channels you grant.'); return; }
    setSending(true); setErr(''); setSentNote('');
    try {
      const r = await api.inviteByEmail(wid, list, addCh, role);
      const chNote = addCh.length ? ` · added to ${addCh.length} channel${addCh.length === 1 ? '' : 's'}` : '';
      const asNote = role === 'guest' ? ' as guest' : '';
      setSentNote(`Invitation${r.sent === 1 ? '' : 's'} sent to ${r.sent} ${r.sent === 1 ? 'person' : 'people'}${asNote}${chNote}.`);
      setEmails('');
    } catch (e: any) { setErr(e?.message || 'Could not send invitations'); }
    finally { setSending(false); }
  };
  const revoke = async (email: string) => {
    if (!window.confirm(`Revoke the invitation for ${email}? Their invite link will stop working.`)) return;
    setRevoking(email); setErr('');
    try {
      await api.revokeInvitation(wid, email);
      setInvites((cur) => cur.filter((i) => i.email !== email));
    } catch (e: any) { setErr(e?.message || 'Could not revoke the invitation'); }
    finally { setRevoking(''); }
  };

  return (
    <div className={embedded ? undefined : 'fx-modal-backdrop'} onClick={embedded ? undefined : onClose}>
      <div className={embedded ? 'fx-people-body' : 'fx-modal fx-modal--invite'} onClick={(e) => e.stopPropagation()}>
        {!embedded && <div className="fx-login-title">Invite people to {workspaceName}</div>}

        <div className="fx-invite-tabs">
          <button type="button" className={`fx-invite-tab${tab === 'invite' ? ' is-on' : ''}`} onClick={() => { setTab('invite'); setErr(''); }}>Invite</button>
          <button type="button" className={`fx-invite-tab${tab === 'people' ? ' is-on' : ''}`} onClick={() => { setTab('people'); setErr(''); }}>
            Invited people{invites.length ? ` · ${invites.length}` : ''}
          </button>
        </div>
        {err && <div className="fx-login-err">{err}</div>}

        {tab === 'invite' && (<>
        <div className="fx-invite-sec">
          <div className="fx-invite-h">Invite by email</div>
          <div className="fx-login-note" style={{ textAlign: 'left', marginTop: 0 }}>
            We’ll email an invite link. They join <strong>{workspaceName}</strong> automatically — no approval needed.
          </div>
          <textarea
            className="fx-text-input fx-invite-emails"
            placeholder="name@company.com, another@company.com"
            value={emails}
            onChange={(e) => setEmails(e.target.value)}
            rows={2}
          />
          <div className="fx-invite-role">
            <div className="fx-seg">
              {(['member', 'guest'] as const).map((r) => (
                <button key={r} type="button" className={`fx-seg-btn${role === r ? ' is-on' : ''}`} onClick={() => setRole(r)}>
                  {r === 'member' ? 'Member' : 'Guest'}
                </button>
              ))}
            </div>
            <div className="fx-login-note" style={{ textAlign: 'left', marginTop: 6, fontSize: 12 }}>
              {role === 'member'
                ? 'Full access to all public channels and the member directory.'
                : 'Restricted to the channels you pick below — no channel browsing, no member directory, can’t create channels.'}
            </div>
          </div>
          {channels.length > 0 && (
            <div className="fx-invite-ch">
              <div className="fx-invite-ch-h">Add to channels <span>({role === 'guest' ? 'required' : 'optional'})</span></div>
              <div className="fx-invite-chips">
                {channels.map((c) => {
                  const on = addCh.includes(c.id);
                  return (
                    <button
                      type="button"
                      key={c.id}
                      className={`fx-invite-chip${on ? ' is-on' : ''}`}
                      onClick={() => toggleCh(c.id)}
                    >
                      <Icon name={c.kind === 'private' ? 'lock' : 'hash'} size={12} /> {c.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {sentNote && <div className="fx-login-note" style={{ color: 'var(--fx-good, #3fbf7f)', marginTop: 6 }}>{sentNote}</div>}
          <button type="button" className="fx-sendbtn fx-login-btn" onClick={sendEmails} disabled={sending}>
            {sending ? 'Sending…' : 'Send invitations'}
          </button>
        </div>

        <div className="fx-invite-sec">
          <div className="fx-invite-h">Or share an invite link</div>
          <div className="fx-login-note" style={{ textAlign: 'left', marginTop: 0 }}>
            Anyone with this link can join directly. Reset it to revoke the old one.
          </div>
          <code className="fx-token">{url || 'Generating link…'}</code>
          <div className="fx-modal-actions">
            <button type="button" className="fx-sendbtn" onClick={copy} disabled={!url}>{copied ? 'Copied ✓' : 'Copy link'}</button>
            <button type="button" className="fx-login-back" onClick={rotate} disabled={rotating || !url}>{rotating ? 'Resetting…' : 'Reset link'}</button>
          </div>
        </div>
        </>)}

        {tab === 'people' && (
          <div className="fx-invite-sec">
            <div className="fx-invite-h">Invited people {invites.length > 0 && <span>({invites.length})</span>}</div>
            {invites.length === 0 ? (
              <div className="fx-login-note" style={{ textAlign: 'left', marginTop: 4 }}>
                No invitations yet — invite people from the Invite tab.
              </div>
            ) : (
              <div className="fx-invite-list">
                {invites.map((iv) => (
                  <div className="fx-invite-row" key={iv.email}>
                    <span className="fx-invite-em">{iv.email}{iv.role === 'guest' ? ' · guest' : ''}</span>
                    <span className="fx-invite-row-right">
                      <span className={`fx-invite-status ${iv.accepted ? 'is-joined' : 'is-pending'}`}>
                        {iv.accepted ? 'Joined' : 'Pending'}
                      </span>
                      {!iv.accepted && (
                        <button type="button" className="fx-invite-revoke" onClick={() => revoke(iv.email)} disabled={revoking === iv.email} title="Revoke invitation">
                          {revoking === iv.email ? '…' : 'Revoke'}
                        </button>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="fx-modal-actions" style={{ marginTop: 4 }}>
          <button type="button" className="fx-login-back" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

function Onboarding({ onCreated }: { onCreated: (ws: Workspaceish) => void }) {
  const [mode, setMode] = useState<'choose' | 'create' | 'join'>('choose');
  const [myReqs, setMyReqs] = useState<MyJoinRequest[]>([]);
  const [org, setOrg] = useState<MyOrg['org']>(null);
  const [joining, setJoining] = useState(false);
  const [orgErr, setOrgErr] = useState('');
  useEffect(() => { api.myJoinRequests().then(setMyReqs).catch(() => {}); }, []);
  useEffect(() => { api.getMyOrg().then((r) => setOrg(r.org)).catch(() => {}); }, []);
  const pending = myReqs.filter((r) => r.status === 'pending');

  const joinOrg = async () => {
    setOrgErr('');
    setJoining(true);
    try { onCreated(await api.joinMyOrg()); } catch (ex: any) { setOrgErr(ex?.message || 'Could not join your org'); setJoining(false); }
  };

  if (mode === 'create') return <CreateWorkspaceForm onBack={() => setMode('choose')} onCreated={onCreated} />;
  if (mode === 'join') {
    return (
      <JoinWorkspaceForm
        onBack={() => setMode('choose')}
        onRequested={(r) => { setMyReqs((cur) => [...cur.filter((x) => x.workspace_id !== r.workspace_id), r]); setMode('choose'); }}
      />
    );
  }
  return (
    <div className="fx-login">
      <div className="fx-login-card fx-onboard">
        <div className="fx-login-logo">F</div>
        <div className="fx-login-title">Welcome to FxM Chat</div>
        <p className="fx-login-note">Create a workspace, or join one that already exists.</p>
        {pending.length > 0 && (
          <div className="fx-onboard-pending">
            ⏳ Waiting for approval to join <b>{pending.map((r) => r.workspace_name).join(', ')}</b> — you'll be let in once an owner or admin approves.
          </div>
        )}
        {org && (
          <button type="button" className="fx-onboard-opt fx-onboard-org" onClick={joinOrg} disabled={joining}>
            <span className="fx-onboard-ic">🏢</span>
            <span className="fx-onboard-txt">
              <b>You're part of {org.org_name}</b>
              <span className="fx-onboard-sub">
                {joining
                  ? (org.already_member ? 'Entering…' : 'Setting up your workspace…')
                  : `${org.already_member ? 'Enter' : 'Join'} your org${org.fma_count > 0 ? ` · ${org.fma_count} FMA${org.fma_count === 1 ? '' : 's'}` : ''}`}
              </span>
            </span>
          </button>
        )}
        {orgErr && <div className="fx-onboard-pending">{orgErr}</div>}
        <button type="button" className="fx-onboard-opt" onClick={() => setMode('create')}>
          <span className="fx-onboard-ic">➕</span>
          <span className="fx-onboard-txt"><b>Create a workspace</b><span className="fx-onboard-sub">Start fresh for your team</span></span>
        </button>
        <button type="button" className="fx-onboard-opt" onClick={() => setMode('join')}>
          <span className="fx-onboard-ic">🔗</span>
          <span className="fx-onboard-txt"><b>Join a workspace</b><span className="fx-onboard-sub">Request access to an existing one</span></span>
        </button>
      </div>
    </div>
  );
}

function CreateWorkspaceForm({ onBack, onCreated }: { onBack: () => void; onCreated: (ws: Workspaceish) => void }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try { onCreated(await api.createWorkspace(name.trim())); } catch { setBusy(false); }
  };
  return (
    <div className="fx-login">
      <form className="fx-login-card fx-onboard" onSubmit={submit}>
        <div className="fx-login-title">Create your workspace</div>
        <input className="fx-text-input fx-login-input" placeholder="Acme Inc" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        <button type="submit" className="fx-sendbtn fx-login-btn" disabled={busy || !name.trim()}>{busy ? 'Creating…' : 'Create workspace'}</button>
        <button type="button" className="fx-login-back" onClick={onBack}>← Back</button>
      </form>
    </div>
  );
}

function JoinWorkspaceForm({ onBack, onRequested }: { onBack: () => void; onRequested: (r: MyJoinRequest) => void }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<DiscoverableWorkspace[]>([]);
  const [busy, setBusy] = useState('');
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return undefined; }
    const t = window.setTimeout(() => api.discoverWorkspaces(q.trim()).then(setResults).catch(() => setResults([])), 250);
    return () => window.clearTimeout(t);
  }, [q]);
  const request = async (w: DiscoverableWorkspace) => {
    setBusy(w.id);
    try { onRequested(await api.requestJoin(w.id)); } catch (ex: any) { window.alert(ex?.message || 'Could not send request'); setBusy(''); }
  };
  return (
    <div className="fx-login">
      <div className="fx-login-card fx-onboard">
        <div className="fx-login-title">Join a workspace</div>
        <input className="fx-text-input fx-login-input" autoFocus placeholder="Search by workspace name…" value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="fx-onboard-results">
          {q.trim().length >= 2 && results.length === 0 && <div className="fx-empty-small">No workspaces match “{q}”</div>}
          {results.map((w) => (
            <div key={w.id} className="fx-onboard-wsrow">
              <span className="fx-chhead-av" style={{ ['--h' as string]: hueOf(w.id) } as React.CSSProperties}>{(w.name[0] || '?').toUpperCase()}</span>
              <span className="fx-onboard-wsname">{w.name}<span className="fx-onboard-sub"> · {w.member_count} member{w.member_count === 1 ? '' : 's'}</span></span>
              {w.request_status === 'pending' ? (
                <span className="fx-onboard-tag">Requested</span>
              ) : (
                <button type="button" className="fx-sendbtn" disabled={busy === w.id} onClick={() => request(w)}>{busy === w.id ? 'Sending…' : 'Request to join'}</button>
              )}
            </div>
          ))}
        </div>
        <button type="button" className="fx-login-back" onClick={onBack}>← Back</button>
      </div>
    </div>
  );
}

function ApprovalsModal({
  workspaceId,
  onClose,
  onCountChange,
  embedded,
}: {
  workspaceId: string;
  onClose: () => void;
  onCountChange?: (n: number) => void;
  embedded?: boolean;
}) {
  const [reqs, setReqs] = useState<JoinRequest[] | null>(null);
  useEffect(() => {
    api.joinRequests(workspaceId).then((r) => { setReqs(r); onCountChange?.(r.length); }).catch(() => setReqs([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);
  const decide = async (id: string, approve: boolean) => {
    try {
      if (approve) await api.approveJoin(workspaceId, id);
      else await api.denyJoin(workspaceId, id);
      setReqs((cur) => {
        const next = (cur || []).filter((x) => x.id !== id);
        onCountChange?.(next.length);
        return next;
      });
    } catch (ex: any) {
      window.alert(ex?.message || 'Could not update the request');
    }
  };
  return (
    <div className={embedded ? undefined : 'fx-modal-backdrop'} onClick={embedded ? undefined : onClose}>
      <div className={embedded ? 'fx-people-body' : 'fx-modal fx-dir'} onClick={(e) => e.stopPropagation()}>
        {!embedded && <div className="fx-login-title">Join requests</div>}
        {reqs === null && <div className="fx-empty-small">Loading…</div>}
        {reqs !== null && reqs.length === 0 && <div className="fx-empty-small">No pending requests.</div>}
        {(reqs || []).map((r) => (
          <div key={r.id} className="fx-req-row">
            <span className="fx-chhead-av" style={avatarStyle(r.user)}>{(r.user.name || r.user.email || '?')[0]?.toUpperCase()}</span>
            <div className="fx-req-main">
              <div className="fx-req-name">{r.user.name || r.user.email}</div>
              {r.message && <div className="fx-req-msg">“{r.message}”</div>}
            </div>
            <button type="button" className="fx-login-back" onClick={() => decide(r.id, false)}>Deny</button>
            <button type="button" className="fx-sendbtn" onClick={() => decide(r.id, true)}>Approve</button>
          </div>
        ))}
        <div className="fx-modal-actions">
          <button type="button" className="fx-login-back" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

type Workspaceish = { id: string; name: string; slug: string; role?: string; features?: Record<string, boolean> };
